import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import { Star, X, ChevronLeft, ChevronRight, ChevronDown, Search, Menu, ShoppingCart, Plus, Minus, Trash2, CheckCircle2, Sparkles } from "lucide-react";
import { validatePhone, sanitizePhone } from "../lib/phoneValidation";
import { Drawer as DrawerPrimitive } from "vaul";

// ---------- Helpers ----------
function resolveImgUrl(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  if (url.startsWith("/static")) {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return base ? `${base}${url}` : url;
  }
  return url;
}

const FALLBACK_BRANDING = {
  menu_primary_color: "#9D00FF",
  menu_secondary_color: "#2D1B4E",
  menu_background_color: "#0A0A0A",
  menu_card_color: "#1A1A1A",
  menu_text_color: "#FFFFFF",
  menu_accent_text_color: "#A0A0A0",
  menu_logo_url: null,
};

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: currentYear - 1939 }, (_, i) => currentYear - i);

function calculateBottleSavings(item) {
  if (!item.has_variants || !item.variants?.length) return null;

  const bottleVariant = item.variants.find(v => /bottle/i.test(v.label || ""));
  if (!bottleVariant) return null;

  const mlVariants = item.variants.filter(v => /\d+\s*ml/i.test(v.label || ""));
  if (!mlVariants.length) return null;

  const peg30 = mlVariants.find(v => /^30\s*ml$/i.test((v.label || "").trim()));
  const pegVariant = peg30 || mlVariants.sort((a, b) => {
    const mlA = parseInt((a.label.match(/(\d+)\s*ml/i) || [])[1] || 999);
    const mlB = parseInt((b.label.match(/(\d+)\s*ml/i) || [])[1] || 999);
    return mlA - mlB;
  })[0];

  const pegMl = parseInt((pegVariant.label.match(/(\d+)\s*ml/i) || [])[1]);
  if (!pegMl) return null;

  const bottleMl = 750;
  const pegsInBottle = Math.ceil(bottleMl / pegMl);
  const costByPeg = pegsInBottle * pegVariant.price;
  const savings = costByPeg - bottleVariant.price;

  return savings > 0 ? { savings } : null;
}

function getLuminance(hex) {
  // Coerce to string (never throws on null/number/object) and drop the leading #.
  let h = String(hex || '#000000').replace('#', '');
  // Expand 3-char shorthand (#fff → ffffff) so slice(4,6) isn't '' → NaN.
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  // Anything unparseable (wrong length, non-hex) → 0, never NaN. The formula and
  // the result for valid 6-char values are unchanged.
  return Number.isNaN(lum) ? 0 : lum;
}

// Text colour for a SOLID primary-coloured fill: dark on light primaries (the warm
// golds/yellows this market picks), white on dark ones. Threshold matches
// --pm-adaptive-text (0.45). Call it inline at each fill site — some render inside
// drawers/overlays outside the subtree where --pm-primary is defined, so a CSS var
// could fail to resolve and produce invisible text; an inline call is deterministic.
function onPrimary(hex) {
  return getLuminance(hex) > 0.45 ? '#1a1a1a' : '#ffffff';
}

// One font size per rendered category grid, sized from the LONGEST name so every
// card in a row shares a size instead of each sizing from its own name length. A
// single long name brings its whole row down to the smaller size — intended:
// consistency across the row is the reported problem. reduce (not a Math.max spread)
// so an empty list yields 0, not -Infinity.
// Sizes are set so a TWO-LINE name (with lineHeight 1.25) fits the card's 4:3 height
// without being clipped by the card edge (#5) — a long name that clipped at 1rem now
// uses 0.95rem, a short-name grid uses 1.15rem.
function nameSizeFor(list) {
  const longest = (list || []).reduce(
    (m, c) => Math.max(m, ((c && c.name) || "").length), 0
  );
  return longest > 14 ? "0.95rem" : "1.15rem";
}

// ---------- Dietary indicator (Indian standard: circle=veg, triangle=non-veg) ----------
function DietDot({ type, className = "mt-0.5" }) {
  if (!type || type === "na") return null;
  const isVeg = type === "veg" || type === "vegan";
  const color = isVeg ? "#22c55e" : "#ef4444";
  return (
    <span
      className={`inline-flex items-center justify-center rounded-sm shrink-0 ${className}`}
      style={{ width: 18, height: 18, border: `1.5px solid ${color}` }}
      title={isVeg ? "Vegetarian" : "Non-vegetarian"}
    >
      {isVeg ? (
        <span className="rounded-full" style={{ width: 9, height: 9, background: color }} />
      ) : (
        <span style={{ width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderBottom: `9px solid ${color}` }} />
      )}
    </span>
  );
}

// ---------- Tag row — resolves keys against catalog, renders icon vs pill ----------
function TagRow({ tagKeys, catalog }) {
  if (!tagKeys?.length) return null;
  const resolved = tagKeys.map(k => catalog?.[k]).filter(Boolean);
  const icons = resolved.filter(t => t.display_style === "icon");
  const pills = resolved.filter(t => t.display_style === "pill");
  if (!icons.length && !pills.length) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap mt-1.5">
      {icons.map(t => (
        <span key={t.label} style={{ fontSize: 15 }} title={t.label}>{t.icon}</span>
      ))}
      {pills.map(t => (
        <span key={t.label} className="text-[11.5px] font-medium px-2.5 py-1 rounded-md border"
              style={{ borderColor: "rgba(128,128,128,0.35)", color: "var(--pm-adaptive-text)" }}>
          {t.icon} {t.label}
        </span>
      ))}
    </div>
  );
}

// ---------- Filter chips (global: All / Veg / Non-Veg / Bestseller) ----------
const FILTER_OPTIONS = [
  { key: "veg",        label: "Veg",        dot: "green" },
  { key: "nonveg",     label: "Non-Veg",    dot: "red" },
  { key: "bestseller", label: "Bestseller", icon: "star" },
  { key: "must_try",   label: "Must Try",   icon: "sparkles" },
  { key: "new",        label: "New" },
];

function FilterChips({ activeFilters, onFilterChange, availableFilters = ["veg", "nonveg", "bestseller"], className = "" }) {
  const toggle = (key) => {
    if (activeFilters.includes(key)) {
      onFilterChange(activeFilters.filter(f => f !== key));
    } else if (key === "veg") {
      onFilterChange([...activeFilters.filter(f => f !== "nonveg"), key]);
    } else if (key === "nonveg") {
      onFilterChange([...activeFilters.filter(f => f !== "veg"), key]);
    } else {
      onFilterChange([...activeFilters, key]);
    }
  };
  const allActive = activeFilters.length === 0;

  return (
    <div className={`flex items-center gap-2 overflow-x-auto scrollbar-hide py-0.5 ${className}`} style={{ fontFamily: "var(--font-body)" }}>
      {/* All chip */}
      <button
        type="button"
        onClick={() => onFilterChange([])}
        className="flex shrink-0 items-center rounded-full border px-4 py-2.5 text-xs font-medium transition-all duration-200"
        style={{
          minHeight: 36,
          borderColor: allActive ? "var(--pm-primary)" : "rgba(255,255,255,0.28)",
          background: allActive ? "var(--pm-primary-light, rgba(157,0,255,0.1))" : "transparent",
          color: allActive ? "var(--pm-primary)" : "var(--pm-adaptive-text)",
        }}
      >
        All
      </button>

      {FILTER_OPTIONS.filter(({ key }) => availableFilters.includes(key)).map(({ key, label, dot, icon }) => {
        const active = activeFilters.includes(key);
        return (
          <button
            key={key}
            type="button"
            onClick={() => toggle(key)}
            className="flex shrink-0 items-center gap-1.5 rounded-full border px-4 py-2.5 text-xs font-medium transition-all duration-200"
            style={{
              minHeight: 36,
              borderColor: active ? "var(--pm-primary)" : "rgba(255,255,255,0.28)",
              background: active ? "var(--pm-primary-light, rgba(157,0,255,0.1))" : "transparent",
              color: active ? "var(--pm-primary)" : "var(--pm-adaptive-text)",
            }}
          >
            {dot === "green" && (
              <span className="flex h-3 w-3 items-center justify-center rounded-sm border border-green-500 shrink-0">
                <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
              </span>
            )}
            {dot === "red" && (
              <span className="flex h-3 w-3 items-center justify-center rounded-sm border border-red-500 shrink-0">
                <span style={{ width: 0, height: 0, borderLeft: "3px solid transparent", borderRight: "3px solid transparent", borderBottom: "5px solid #ef4444" }} />
              </span>
            )}
            {icon === "star" && <Star size={9} fill="currentColor" className="shrink-0" />}
            {icon === "sparkles" && <Sparkles size={9} className="shrink-0" />}
            {label}
          </button>
        );
      })}
    </div>
  );
}

// Rule text for an addon group from its LINK's min/max. Defensive: malformed values
// (missing, non-integer, negative, max<min) never render a broken rule like
// "Choose 3–1" — they fall back to "Optional" (min 0/absent) or "Required".
function addonRuleText(min, max) {
  const mi = Number(min), ma = Number(max);
  const miOk = Number.isInteger(mi) && mi >= 0;
  const maOk = Number.isInteger(ma) && ma >= 0;
  const minZeroOrAbsent = (min === null || min === undefined || (miOk && mi === 0));
  if (!miOk || !maOk || ma < mi) return minZeroOrAbsent ? "Optional" : "Required";
  if (mi >= 1 && ma === mi) return `Choose ${mi}`;            // 1/1, N/N
  if (mi >= 1 && ma > mi) return `Choose ${mi}–${ma}`;   // M–N (en dash)
  if (mi === 0 && ma > 1) return `Optional · up to ${ma}`;
  return "Optional";                                          // 0/1, 0/0
}

// Resolve which addon groups an item shows, from its item-level and variant-level
// links, against the served addon_catalog. Returns
//   [{ id, name, display_order, items, min, max, qualifier }]
// deduped by group_id (item-level, else first link, wins the min/max). A group on
// EVERY variant is treated as item-level (no qualifier); on SOME variants it carries
// a "With <labels>" qualifier. Groups that don't resolve or have no items are
// skipped. Required groups (min>=1) sort first, then optional, each by display_order.
// Never throws.
function resolveAddonGroups(item, addonCatalog) {
  if (!addonCatalog || !item) return [];
  try {
    const byId = new Map();   // group_id -> { id, name, display_order, items, min, max, itemLevel, variants:Set }
    const add = (link, variantLabel) => {
      const gid = link && link.group_id;
      if (!gid) return;
      const g = addonCatalog[gid];
      if (!g || !Array.isArray(g.items) || g.items.length === 0) return;  // unresolved / empty
      let e = byId.get(gid);
      if (!e) {
        e = { id: gid, name: g.name, display_order: g.display_order, items: g.items,
              min: link.min, max: link.max, itemLevel: false, variants: new Set() };
        byId.set(gid, e);
      }
      if (variantLabel == null) e.itemLevel = true;   // first link (item-level) keeps its min/max
      else e.variants.add(variantLabel);
    };
    for (const link of (item.addon_groups || [])) add(link, null);
    const variants = Array.isArray(item.variants) ? item.variants : [];
    variants.forEach((v) => { for (const link of (v?.addon_groups || [])) add(link, v?.label ?? ""); });
    const out = [];
    for (const e of byId.values()) {
      let qualifier = null;
      if (!e.itemLevel && variants.length > 0 && e.variants.size > 0 && e.variants.size < variants.length) {
        const labels = variants.map(v => v?.label ?? "").filter(l => e.variants.has(l));
        qualifier = `With ${labels.join(", ")}`;
      }
      out.push({ id: e.id, name: e.name, display_order: e.display_order, items: e.items,
                 min: e.min, max: e.max, qualifier });
    }
    out.sort((a, b) => {
      const ar = Number(a.min) >= 1 ? 0 : 1;
      const br = Number(b.min) >= 1 ? 0 : 1;
      if (ar !== br) return ar - br;
      return (a.display_order ?? 0) - (b.display_order ?? 0);
    });
    return out;
  } catch {
    return [];
  }
}

// Add-on groups applicable to ONE specific variant selection: item-level links plus
// the chosen variant's own, deduped with item-level winning, required (min>=1) first.
// Same merge rules as resolveAddonGroups, but scoped to the selected variant (which the
// display-only resolver can't express) — the ordering sheet and the server price against
// exactly this set, so they must agree. Returns [{id, name, display_order, items, min, max}].
function applicableGroups(item, variant, addonCatalog) {
  if (!addonCatalog || !item) return [];
  const links = [...(item.addon_groups || []), ...(variant ? (variant.addon_groups || []) : [])];
  const byId = new Map();
  for (const lk of links) {
    const gid = lk && lk.group_id;
    if (!gid || byId.has(gid)) continue;
    const g = addonCatalog[gid];
    if (!g || !Array.isArray(g.items) || g.items.length === 0) continue;
    byId.set(gid, { id: gid, name: g.name, display_order: g.display_order,
                    items: g.items, min: lk.min, max: lk.max });
  }
  return [...byId.values()].sort((a, b) => {
    const ar = Number(a.min) >= 1 ? 0 : 1;
    const br = Number(b.min) >= 1 ? 0 : 1;
    if (ar !== br) return ar - br;
    return (a.display_order ?? 0) - (b.display_order ?? 0);
  });
}

// A cart line's identity. Deterministic and order-independent: item, then variant, then
// the add-on option ids sorted before joining — so "oat then syrup" and "syrup then oat"
// collapse to ONE line. Lines match only on an identical key.
function cartLineKey(itemId, variantId, addonOptionIds) {
  return `${itemId}|${variantId || ""}|${[...(addonOptionIds || [])].sort().join(",")}`;
}

// Human, guest-facing label for an order type, lower-case for mid-sentence use ("not available for
// takeaway"). In India "Takeaway" is the word every guest already knows; AD-2's "collection" was a
// British term that landed worse than the problem it solved (AE-2). The stored value is "takeaway" too.
function orderTypeLabel(t) {
  return t === "dine_in" ? "dine-in" : t === "room" ? "room service" : "takeaway";
}

// Card tag for an item limited to a single order type, shown on an unscoped "both" menu before the
// guest has chosen (AD-1) so a one-type dish is visible-but-marked rather than silently filtered.
// null when it's available either way (no tag needed) or available_for is absent.
function orderTypeTag(availableFor) {
  if (!Array.isArray(availableFor)) return null;
  const dineIn = availableFor.includes("dine_in");
  const takeaway = availableFor.includes("takeaway");
  if (dineIn && !takeaway) return "Dine-in only";
  if (takeaway && !dineIn) return "Takeaway only";
  return null;
}

// Is an item orderable for `orderType`? It is whenever it carries NO usable restriction — available_for
// absent, or an empty array (the venue gave no channel information) — or when it explicitly lists the
// type. A missing OR empty field must never hide an item: an empty array marked every item "available
// nowhere" and blanked whole browse-only menus in production (AG), because service points (hence
// channels) only exist on venues that order. The order-time check in the API is the real guarantee, so
// we fail toward showing. A NON-empty array that omits the type is a real restriction and still hides
// (e.g. a Takeaway-excluded dish on a ?type=takeaway link). Every place that filters or counts on
// available_for goes through here so the count and the list can never disagree again.
function availableForType(availableFor, orderType) {
  if (!Array.isArray(availableFor) || availableFor.length === 0) return true;
  return availableFor.includes(orderType);
}

// Cart is persisted to localStorage so a phone lock or reload doesn't destroy an order in
// progress. Keyed by "<slug>:<context>" (see cartStorageKey): a table order and a room order
// (?context=room) are NOT interchangeable — a room order lands on a room tab and settles via
// Room Bills — so their carts must never share storage, or a stale table cart could land on a
// hotel guest's room bill. All access is wrapped — private mode / quota / disabled storage
// must never break the page; the cart simply stays in memory then.
const CART_STORAGE_PREFIX = "pm-cart:";
// A restored cart is aged from the moment it was last saved (see the tiers in the restore
// effect): under an hour it comes back silently, up to four hours it comes back but the guest
// is prompted to review or clear it, and beyond that it's discarded as almost certainly a
// previous visit (a table or room QR is reused by successive guests all day).
const CART_REVIEW_AGE_MS = 60 * 60 * 1000;       // 1h — below this, restore silently
const CART_MAX_AGE_MS    = 4 * 60 * 60 * 1000;   // 4h — above this, discard
// Returns { lines, savedAt }. savedAt is null for a cart written before this timestamp
// existed (a bare array) or with no usable timestamp — the restore effect treats a null
// savedAt as expired rather than trusting an unknown age.
function loadStoredCart(key) {
  try {
    const raw = window.localStorage.getItem(CART_STORAGE_PREFIX + key);
    if (!raw) return { lines: [], savedAt: null };
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return { lines: parsed, savedAt: null };  // pre-timestamp shape
    const lines = Array.isArray(parsed?.lines) ? parsed.lines : [];
    const savedAt = typeof parsed?.savedAt === "number" ? parsed.savedAt : null;
    return { lines, savedAt };
  } catch { return { lines: [], savedAt: null }; }
}
function saveStoredCart(key, cart) {
  try {
    if (!cart || cart.length === 0) window.localStorage.removeItem(CART_STORAGE_PREFIX + key);
    else window.localStorage.setItem(CART_STORAGE_PREFIX + key, JSON.stringify({ savedAt: Date.now(), lines: cart }));
  } catch { /* ignore — cart stays in memory only */ }
}

// Re-resolve a stored cart line against the freshly loaded menu: drop it if the item or
// its variant no longer exists (a menu edit / POS delete), and re-price the base from the
// current menu. Add-on prices are kept as stored (the server re-prices authoritatively at
// checkout). Returns a fresh line or null to drop it.
function reconcileCartLine(line, itemsById) {
  const item = itemsById[line.item_id];
  if (!item) return null;
  let base;
  if (line.variant_id) {
    const v = (item.variants || []).find(x => x.id === line.variant_id);
    if (!v) return null;
    base = Number(v.price) || 0;
  } else {
    if (item.has_variants) return null;  // item became variant-only since it was added
    base = Number(item.price) || 0;
  }
  const addons = Array.isArray(line.addons) ? line.addons : [];
  const addonTotal = addons.reduce((s, a) => s + (Number(a.price) || 0), 0);
  return {
    ...line,
    name: item.name,
    unit_price: Math.round((base + addonTotal) * 100) / 100,
    qty: Math.max(1, parseInt(line.qty, 10) || 1),
  };
}

// Price / variant rendering — ONE renderer shared by the card (compact inline line)
// and the item view (stacked=true → each variant on its own readable row with a
// hairline). Same per-variant content + points logic either way; never a second
// renderer. Module-level so both ItemCard and the hoisted ItemView can use it.
function PriceBlock({ item, branding, loyaltyEnabled, stacked }) {
  const primary = branding.menu_primary_color;
  // On the CARD (compact, !stacked), 3+ variants rendered inline wrap to 4+ lines in a ~222px
  // column (Naan: 5 variants ≈ 130 chars) before the description even starts. At 3+ collapse to
  // a price range + count, in the SAME size/colour/weight as the inline list so the two forms
  // sit together in a list without looking like different components. 1–2 variants read well
  // inline (93/95 variant items have exactly 2) and are unchanged, as is the stacked item sheet,
  // which always lists every variant on its own row.
  const variantPrices = (item.variants || []).map(v => Number(v.price) || 0);
  return (item.has_variants && item.variants?.length > 0) ? (
    (!stacked && item.variants.length >= 3) ? (
      <div className="mt-1.5" style={{ fontFamily: "var(--font-body)", fontSize: "0.82rem", color: "var(--pm-muted)", fontWeight: 400 }}>
        <span style={{ color: primary, fontWeight: 600 }}>₹{Math.min(...variantPrices)} – ₹{Math.max(...variantPrices)}</span> · {item.variants.length} options
      </div>
    ) : (
    <div
      className={stacked ? "mt-1.5 flex flex-col" : "mt-1.5 flex flex-wrap items-center gap-x-0 gap-y-1"}
      style={{ fontFamily: "var(--font-body)", fontSize: stacked ? "0.95rem" : "0.82rem", color: "var(--pm-muted)", fontWeight: 400 }}
    >
      {item.variants.map((v, idx) => (
        <React.Fragment key={idx}>
          {!stacked && idx > 0 && (
            <span style={{ margin: "0 6px", opacity: 0.3, fontSize: "0.75rem" }}>|</span>
          )}
          {stacked ? (
            /* ── Item view: one variant per row, label left / price right ── */
            <span style={{
              display: "flex", justifyContent: "space-between", gap: 16, alignItems: "baseline",
              paddingTop: idx > 0 ? 8 : 0,
              borderTop: idx > 0 ? "1px solid color-mix(in srgb, var(--pm-adaptive-text) 12%, transparent)" : "none",
            }}>
              <span>{v.label}</span>
              <span style={{ whiteSpace: "nowrap" }}>
                <span style={{ color: primary, fontWeight: 600 }}>₹{v.price}</span>
                {loyaltyEnabled && v.points_display > 0 && (
                  <span style={{ color: primary, opacity: 0.85 }}> · Earn {v.points_display}pts</span>
                )}
              </span>
            </span>
          ) : (
            /* ── Card: compact inline "30 ML ₹390 · Earn 39pts | …" ── */
            <span>
              {v.label}{" "}
              <span style={{ color: primary, fontWeight: 600 }}>₹{v.price}</span>
              {loyaltyEnabled && v.points_display > 0 && (
                <span style={{ color: primary, opacity: 0.85 }}> · Earn {v.points_display}pts</span>
              )}
            </span>
          )}
        </React.Fragment>
      ))}
    </div>
    )
  ) : (
    /* ── Single price (default) ── */
    <div className="flex items-center gap-3 mt-1.5">
      <span
        className="font-bold"
        style={{ color: primary, fontFamily: "var(--font-editorial)", fontSize: stacked ? "1.5rem" : "1.2rem", fontWeight: 700 }}
      >
        ₹{item.price}
      </span>
      {loyaltyEnabled && item.points_display > 0 && (
        <span style={{ color: "var(--pm-primary)", fontFamily: "var(--font-body)", fontSize: stacked ? "0.85rem" : "0.78rem", fontWeight: 500 }}>
          Earn {item.points_display} pts
        </span>
      )}
    </div>
  );
}

// Full-screen item view (photo + details), portalled to <body>. Hoisted OUT of ItemCard:
// Vaul unmounts Drawer.Content's children the moment the drawer starts closing, so a view
// living inside a card inside the drawer would be torn down under it. It is rendered once
// at page level (driven by ?item) and mounted only while shown, so its effects key on
// mount rather than on an open flag. The panel carries the VENUE's own background + tokens
// (not a hardcoded black/white overlay); only the scrim behind it is dark, for focus.
function ItemView({ item, branding, loyaltyEnabled, catalog, addonCatalog, canOrder, editLine, onSubmit, onClose }) {
  const primary = branding.menu_primary_color;
  const hasImage = Boolean(item.image_url);
  const soldOut = !item.is_available;
  // Stable ref (per item) — it's a dependency of the add-on pruning effect below.
  const variants = useMemo(() => item.variants || [], [item]);
  const hasVariants = Boolean(item.has_variants) && variants.length > 0;
  // Merged (all-variant) groups for the display-only view; the interactive sheet computes
  // a variant-scoped set (see `groups`) so the shown rules follow the chosen size.
  const displayGroups = resolveAddonGroups(item, addonCatalog);
  const hasAddons = displayGroups.length > 0;
  // The view portals outside pm-root, so it can't inherit the menu's CSS vars — it
  // re-declares them from the venue's own tokens (below), including this adaptive text
  // colour (dark on a light venue background, white on a dark one), matching the drawer.
  const adaptiveText = getLuminance(branding.menu_background_color || "#000000") > 0.45 ? "#1a1a1a" : "#ffffff";
  const hairline = "1px solid color-mix(in srgb, var(--pm-adaptive-text) 12%, transparent)";
  // Interactive customisation only when the venue takes QR orders and the item is in
  // stock; otherwise this stays the read-only view it was before ordering existed.
  const interactive = Boolean(canOrder) && !soldOut;

  // --- Selection (used only when interactive). Prefilled from editLine when editing an
  //     existing cart line; a variant item defaults to its first variant. ---
  // Track the selected variant by INDEX, not by id: a pre-existing item can have null
  // variant ids (until the backfill runs), and comparing on a null id freezes the selector
  // and can silently select the wrong variant. An index is always unique and present, so
  // the selector always shows one option chosen and every tap changes it. The real id
  // (which the cart key and the server need) is read from the chosen variant; a missing one
  // is caught loudly in submit() rather than shipping a wrong order.
  const initialVariantIdx = (() => {
    if (!hasVariants) return -1;
    if (editLine?.variant_id) {
      const i = variants.findIndex(v => v.id === editLine.variant_id);
      if (i >= 0) return i;
    }
    return 0;
  })();
  const [selVariantIdx, setSelVariantIdx] = useState(initialVariantIdx);
  const [selAddonIds, setSelAddonIds] = useState(() => new Set(editLine?.addon_option_ids || []));
  const [qty, setQty] = useState(Math.max(1, editLine?.qty || 1));
  const [invalidGroupId, setInvalidGroupId] = useState(null);

  const chosenVariant = (hasVariants && selVariantIdx >= 0) ? (variants[selVariantIdx] || null) : null;
  const groups = interactive ? applicableGroups(item, chosenVariant, addonCatalog) : [];

  // Bottom-fade scroll cue: the scrollbar is hidden on touch, so a fade over the
  // scroller's last ~40px is the only signal that options continue below. Shown only
  // while there is content past the fold; recomputed on scroll, resize and layout.
  const scrollRef = useRef(null);
  const [showScrollFade, setShowScrollFade] = useState(false);
  const updateScrollFade = useCallback(() => {
    const el = scrollRef.current;
    if (!el) { setShowScrollFade(false); return; }
    setShowScrollFade(el.scrollTop + el.clientHeight < el.scrollHeight - 2);
  }, []);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollFade();
    // A ResizeObserver catches the scroller shrinking once the top photo loads and the
    // flex column re-lays-out; the resize listener catches viewport/orientation change.
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => updateScrollFade()) : null;
    if (ro) ro.observe(el);
    window.addEventListener("resize", updateScrollFade);
    return () => { if (ro) ro.disconnect(); window.removeEventListener("resize", updateScrollFade); };
  }, [updateScrollFade]);

  useEffect(() => {
    // Consume Escape in the CAPTURE phase so only the topmost layer (this view) closes;
    // the category drawer beneath has its own document-level Escape listener that would
    // otherwise also fire. A bubble-phase stopPropagation is too late — the drawer's
    // document listener runs before a plain window listener — so we capture, then stop
    // propagation + default, so nothing below reacts.
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  // Changing the variant can make some chosen add-ons inapplicable — prune them so a stale
  // option never survives into the price or the cart line.
  useEffect(() => {
    if (!interactive) return;
    const cv = (hasVariants && selVariantIdx >= 0) ? (variants[selVariantIdx] || null) : null;
    const offered = new Set();
    for (const g of applicableGroups(item, cv, addonCatalog)) for (const o of g.items) offered.add(o.id);
    setSelAddonIds(prev => {
      let changed = false;
      const next = new Set();
      prev.forEach(id => { if (offered.has(id)) next.add(id); else changed = true; });
      return changed ? next : prev;
    });
  }, [selVariantIdx, interactive, hasVariants, variants, item, addonCatalog]);

  // Resolve the selection to priced options (offered + active only) — pricing and the cart
  // line ignore anything stale.
  const selectedOptions = [];
  let addonTotal = 0;
  for (const g of groups) {
    for (const o of g.items) {
      if (selAddonIds.has(o.id) && o.is_active !== false) {
        const p = Number(o.price) || 0;
        selectedOptions.push({ id: o.id, name: o.name, price: p, group_id: g.id });
        addonTotal += p;
      }
    }
  }
  const basePrice = chosenVariant ? (Number(chosenVariant.price) || 0) : (Number(item.price) || 0);
  const unitPrice = Math.round((basePrice + addonTotal) * 100) / 100;
  const totalPrice = Math.round(unitPrice * qty * 100) / 100;

  const toggleOption = (g, opt) => {
    if (opt.is_active === false) return;
    const mx = Number(g.max) || 0;
    const groupIds = g.items.map(o => o.id);
    setInvalidGroupId(null);
    setSelAddonIds(prev => {
      const next = new Set(prev);
      if (next.has(opt.id)) { next.delete(opt.id); return next; }
      if (mx === 1) {
        groupIds.forEach(id => next.delete(id));   // single-select group: replace
        next.add(opt.id);
      } else {
        const inGroup = groupIds.filter(id => next.has(id)).length;
        if (mx > 0 && inGroup >= mx) return prev;   // at the cap — ignore the extra tap
        next.add(opt.id);
      }
      return next;
    });
  };

  const submit = () => {
    // Loud guard: a variant item whose chosen variant has no id (pre-backfill data) cannot
    // be priced by the server and would collide with its siblings in the cart. Block it with
    // a clear message rather than shipping a wrong or silently-merged order line.
    if (hasVariants && !(chosenVariant && chosenVariant.id)) {
      toast.error("This item can't be ordered right now — please ask staff.");
      return;
    }
    // Every applicable group's min/max must hold; on a miss, surface it and scroll the
    // first offending group into view rather than letting the server reject the order.
    for (const g of groups) {
      const cnt = g.items.filter(o => selAddonIds.has(o.id) && o.is_active !== false).length;
      const mn = Number(g.min) || 0, mx = Number(g.max) || 0;
      if (cnt < mn || (mx > 0 && cnt > mx)) {
        setInvalidGroupId(g.id);
        const el = scrollRef.current && scrollRef.current.querySelector(`[data-pm-group="${g.id}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
    }
    onSubmit({
      item,
      variant: chosenVariant,
      addonOptionIds: selectedOptions.map(o => o.id),
      addons: selectedOptions.map(o => ({ id: o.id, name: o.name, price: o.price })),
      unitPrice,
      qty,
      note: editLine?.note || "",
    });
  };

  // One selectable option row — radio (single-select group / variants) or checkbox (multi).
  // A plain render function (not a nested component) so selecting an option doesn't remount
  // every row. `keyId` becomes the element key in the surrounding .map().
  const optionRow = ({ keyId, selected, radio, disabled, onSelect, dot, label, right, first }) => (
    <button
      key={keyId}
      type="button"
      disabled={disabled}
      onClick={disabled ? undefined : onSelect}
      style={{
        display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
        background: "none", border: "none", padding: "10px 0",
        borderTop: first ? "none" : hairline,
        color: "var(--pm-adaptive-text)", cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1, fontFamily: "var(--font-body)", fontSize: "0.95rem",
      }}
    >
      <span aria-hidden="true" style={{
        width: 18, height: 18, flexShrink: 0,
        borderRadius: radio ? 999 : 5,
        border: `2px solid ${selected ? primary : "color-mix(in srgb, var(--pm-adaptive-text) 35%, transparent)"}`,
        background: (!radio && selected) ? primary : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {radio && selected && <span style={{ width: 8, height: 8, borderRadius: 999, background: primary }} />}
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flex: 1, minWidth: 0 }}>
        {dot && <DietDot type={dot} className="" />}
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
      </span>
      {right && <span style={{ whiteSpace: "nowrap", flexShrink: 0 }}>{right}</span>}
    </button>
  );

  return createPortal(
    <div
      data-pm-item-view="true"
      onClick={(e) => { e.stopPropagation(); onClose(); }}
      onPointerDown={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      data-testid={`pm-item-view-${item.id}`}
      style={{
        position: "fixed", inset: 0, zIndex: 80, background: "rgba(0,0,0,0.8)",
        pointerEvents: "auto",
        display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem",
      }}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close"
        style={{ position: "absolute", top: 16, right: 16, zIndex: 2, width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 999, background: "rgba(0,0,0,0.55)", color: "#fff", border: "1px solid rgba(255,255,255,0.35)" }}
      >
        <X size={20} />
      </button>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 480, maxHeight: "92dvh",
          display: "flex", flexDirection: "column",
          background: branding.menu_background_color, color: adaptiveText,
          borderRadius: 16, overflow: "hidden", fontFamily: "var(--font-body)",
          // Venue tokens for the reused TagRow / price renderer inside this portal.
          "--pm-primary": primary,
          "--pm-muted": branding.menu_accent_text_color,
          "--pm-adaptive-text": adaptiveText,
        }}
      >
        {/* Photo — only when present (most items have none). Full width, never
            cropped, ≤50vh, fixed at the top; details below scroll. */}
        {hasImage && (
          <img
            src={resolveImgUrl(item.image_url)}
            alt={item.name}
            style={{ width: "100%", maxHeight: "50vh", objectFit: "contain", display: "block", flexShrink: 0, background: branding.menu_card_color }}
          />
        )}
        {/* Scroller + bottom fade cue. The wrapper is the positioning context for the
            fade, which overlays the scroller's last rows. data-pm-scroll opts this
            element back into a slim themed scrollbar (index.css). */}
        <div style={{ position: "relative", flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column" }}>
        <div
          ref={scrollRef}
          data-pm-scroll="true"
          onScroll={updateScrollFade}
          style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", padding: "18px 20px 22px" }}
        >
          {/* Name + veg indicator centred to the name's FIRST line (fixed-height
              box, not baseline) — holds whether the name is one line or two. */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <span style={{ display: "inline-flex", alignItems: "center", height: "calc(1.5rem * 1.3)", flexShrink: 0 }}>
              <DietDot type={item.item_type} className="" />
            </span>
            <h2 style={{ color: adaptiveText, fontFamily: "var(--font-editorial)", fontSize: "1.5rem", fontWeight: 600, lineHeight: 1.3, margin: 0 }}>
              {item.name}
            </h2>
          </div>
          <TagRow tagKeys={item.tags} catalog={catalog} />

          {interactive ? (
            <>
              {/* Variant single-select. A variant item shows its choices here (each with
                  its own price); a simple item just shows its base price line. */}
              {hasVariants ? (
                <div className="mt-3" data-pm-group="__variant__" style={{
                  borderRadius: 12,
                  outline: invalidGroupId === "__variant__" ? `1px solid #ef4444` : "none",
                  outlineOffset: 6,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
                    <span style={{ color: adaptiveText, fontWeight: 600, fontSize: "0.95rem" }}>Choose one</span>
                    <span style={{ color: "var(--pm-muted)", fontSize: "0.8rem" }}>Required</span>
                  </div>
                  <div className="mt-1">
                    {variants.map((v, vi) => optionRow({
                      keyId: v.id || `idx:${vi}`,
                      first: vi === 0,
                      radio: true,
                      selected: selVariantIdx === vi,
                      onSelect: () => { setSelVariantIdx(vi); setInvalidGroupId(null); },
                      label: v.label,
                      right: (
                        <span>
                          <span style={{ color: primary, fontWeight: 600 }}>₹{v.price}</span>
                          {loyaltyEnabled && v.points_display > 0 && (
                            <span style={{ color: primary, opacity: 0.85, fontSize: "0.8rem" }}> · {v.points_display}pts</span>
                          )}
                        </span>
                      ),
                    }))}
                  </div>
                </div>
              ) : (
                <div className="mt-3"><PriceBlock item={item} branding={branding} loyaltyEnabled={loyaltyEnabled} stacked /></div>
              )}

              {/* Add-on groups — selectable. Required groups first (from applicableGroups). */}
              {groups.map((g) => {
                const mx = Number(g.max) || 0;
                const isRadio = mx === 1;
                const selInGroup = g.items.filter(o => selAddonIds.has(o.id)).length;
                const atCap = mx > 0 && selInGroup >= mx;
                return (
                  <div
                    key={g.id}
                    data-pm-group={g.id}
                    style={{
                      marginTop: 16, paddingTop: 16, borderTop: hairline,
                      outline: invalidGroupId === g.id ? "1px solid #ef4444" : "none",
                      outlineOffset: 6, borderRadius: 12,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
                      <span style={{ color: adaptiveText, fontWeight: 600, fontSize: "0.95rem" }}>{g.name}</span>
                      <span style={{ color: invalidGroupId === g.id ? "#ef4444" : "var(--pm-muted)", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                        {addonRuleText(g.min, g.max)}
                      </span>
                    </div>
                    <div className="mt-1">
                      {g.items.map((opt, oi) => {
                        const inactive = opt.is_active === false;
                        const selected = selAddonIds.has(opt.id);
                        return optionRow({
                          keyId: opt.id,
                          first: oi === 0,
                          radio: isRadio,
                          selected,
                          disabled: inactive || (!selected && !isRadio && atCap),
                          onSelect: () => toggleOption(g, opt),
                          dot: opt.item_type && opt.item_type !== "veg" ? opt.item_type : null,
                          label: opt.name,
                          right: (
                            inactive
                              ? <span style={{ fontSize: "0.8rem", color: "var(--pm-muted)" }}>Unavailable</span>
                              : Number(opt.price) > 0
                                ? <span style={{ color: primary, fontWeight: 600 }}>+₹{opt.price}</span>
                                : <span style={{ fontSize: "0.8rem", color: "var(--pm-muted)" }}>{Number(g.min) >= 1 ? "Included" : "Free"}</span>
                          ),
                        });
                      })}
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <>
              {/* Display-only (no ordering / sold out): unchanged read-only rendering. */}
              <div className="mt-3"><PriceBlock item={item} branding={branding} loyaltyEnabled={loyaltyEnabled} stacked /></div>
              {soldOut && (
                <p className="mt-3 text-xs font-bold uppercase tracking-wider" style={{ color: "#ef4444" }}>
                  Currently sold out
                </p>
              )}
              {hasAddons && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: hairline }}>
                  {displayGroups.map((g, gi) => {
                    const rule = addonRuleText(g.min, g.max) + (g.qualifier ? ` · ${g.qualifier}` : "");
                    const groupHasPriced = g.items.some(o => Number(o.price) > 0);
                    const groupRequired = Number(g.min) >= 1;
                    return (
                      <div key={g.id} style={{ marginTop: gi > 0 ? 18 : 0 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
                          <span style={{ color: adaptiveText, fontWeight: 600, fontSize: "0.95rem" }}>{g.name}</span>
                          <span style={{ color: "var(--pm-muted)", fontSize: "0.8rem", whiteSpace: "nowrap" }}>{rule}</span>
                        </div>
                        <div className="mt-1.5 flex flex-col" style={{ fontFamily: "var(--font-body)", fontSize: "0.95rem", color: "var(--pm-muted)" }}>
                          {g.items.map((opt, oi) => {
                            const inactive = opt.is_active === false;
                            const showDot = opt.item_type && opt.item_type !== "veg";
                            let right = null;
                            if (inactive) {
                              right = <span style={{ fontSize: "0.8rem" }}>Unavailable</span>;
                            } else if (Number(opt.price) > 0) {
                              right = <span style={{ color: primary, fontWeight: 600 }}>+₹{opt.price}</span>;
                            } else if (groupHasPriced) {
                              right = <span style={{ fontSize: "0.8rem" }}>{groupRequired ? "Included" : "Free"}</span>;
                            }
                            return (
                              <span key={opt.id} style={{
                                display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center",
                                paddingTop: oi > 0 ? 8 : 0,
                                borderTop: oi > 0 ? hairline : "none",
                                opacity: inactive ? 0.5 : 1,
                              }}>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                  {showDot && <DietDot type={opt.item_type} className="" />}
                                  <span>{opt.name}</span>
                                </span>
                                {right && <span style={{ whiteSpace: "nowrap" }}>{right}</span>}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Description — untruncated, separated from the price block. */}
          {item.description && (
            <p style={{
              marginTop: 16, paddingTop: 16, borderTop: hairline,
              fontSize: "0.9rem", lineHeight: 1.55, color: "var(--pm-muted)",
            }}>
              {item.description}
            </p>
          )}
        </div>
        {/* Bottom fade — transparent → the panel's own venue background, over the
            scroller's last ~40px. Shown only while content continues below; never
            blocks a tap. */}
        {showScrollFade && (
          <div aria-hidden="true" style={{
            position: "absolute", left: 0, right: 0, bottom: 0, height: 40,
            background: `linear-gradient(to bottom, ${branding.menu_background_color}00, ${branding.menu_background_color})`,
            pointerEvents: "none",
          }} />
        )}
        </div>

        {/* Sticky footer CTA — qty stepper + the computed total. Only when orderable. */}
        {interactive && (
          <div style={{
            flexShrink: 0, display: "flex", alignItems: "center", gap: 12,
            padding: "12px 16px calc(12px + env(safe-area-inset-bottom, 0px))",
            borderTop: hairline, background: branding.menu_background_color,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              <button type="button" aria-label="Decrease quantity"
                onClick={() => setQty(q => Math.max(1, q - 1))}
                style={{ width: 34, height: 34, borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center",
                         background: "transparent", border: `1px solid color-mix(in srgb, var(--pm-adaptive-text) 30%, transparent)`, color: adaptiveText }}>
                <Minus size={15} />
              </button>
              <span style={{ minWidth: 18, textAlign: "center", fontWeight: 700, color: adaptiveText }}>{qty}</span>
              <button type="button" aria-label="Increase quantity"
                onClick={() => setQty(q => q + 1)}
                style={{ width: 34, height: 34, borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center",
                         background: "transparent", border: `1px solid color-mix(in srgb, var(--pm-adaptive-text) 30%, transparent)`, color: adaptiveText }}>
                <Plus size={15} />
              </button>
            </div>
            <button
              type="button"
              onClick={submit}
              data-testid="pm-item-add"
              style={{
                flex: 1, minHeight: 48, borderRadius: 12, border: "none", cursor: "pointer",
                background: primary, color: onPrimary(primary), fontWeight: 700, fontSize: "0.95rem",
                display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 18px",
                fontFamily: "var(--font-body)",
              }}
            >
              <span>{editLine ? "Update" : "Add"}{qty > 1 ? ` ${qty}` : ""}</span>
              <span>₹{totalPrice.toFixed(0)}</span>
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ---------- Item card ----------
function ItemCard({ item, branding, bottleMenuPromo, loyaltyEnabled, orderingQrEnabled, onOpen, onQuickAdd, cartQty = 0, onUpdateQty, catalog, addonCatalog }) {
  const primary = branding.menu_primary_color;
  const hasImage = Boolean(item.image_url);
  const soldOut = !item.is_available;
  // Addon groups this item shows (item-level + variant-level, resolved against the
  // served catalog). A no-op for native items (addon_groups: []) and venues with no
  // addon_catalog.
  const addonGroups = resolveAddonGroups(item, addonCatalog);
  const hasAddons = addonGroups.length > 0;
  // A variant item has no single price, and an item with add-on groups needs choices, so
  // neither can be one-tap added — tapping the card opens the customisation sheet, which
  // prices the selection (like Swiggy/Zomato). Only a plain item gets the inline quick-add.
  const hasChoices = Boolean(item.has_variants) || hasAddons;
  const canQuickAdd = orderingQrEnabled && !soldOut && !hasChoices;
  // Bug 2: a customisable item (variants / add-ons) can't be one-tap added, but it must
  // still show an affordance — an ADD button that opens the sheet, so every orderable card
  // looks orderable, not like a printed-menu row.
  const showCustomiseAdd = orderingQrEnabled && !soldOut && hasChoices;
  // On an ordering venue the ADD button carries its own "customisable" caption, so the
  // price-row "Customisable ›" cue would double up — keep it only for display-only venues
  // (no button), where it is the sole hint. Coloured primary as the interactive cue.
  const showCustomisableCue = hasAddons && !orderingQrEnabled;
  const customisableColor = "var(--pm-primary)";
  // The deterministic key of this item's plain (no variant / no add-ons) cart line, so the
  // card's quick-add stepper reflects and mutates exactly that line.
  const simpleLineKey = cartLineKey(item.id, null, []);
  // The full-screen item view is rendered once at page level and driven by the URL
  // (?item). The card only asks to open it (onOpen), which pushes ?item so the browser
  // back button and the on-screen close stay in lockstep.

  // Order-bar handlers. The whole card is a button (it opens the item sheet), so every
  // control in the bar stops propagation — a tap on Add / the stepper must not also open
  // the sheet.
  const handleQuickAdd = useCallback((e) => {
    e.stopPropagation();
    onQuickAdd(item);
    toast.success(`${item.name} added`, { duration: 1200 });
  }, [onQuickAdd, item]);
  const handleInc = useCallback((e) => { e.stopPropagation(); onQuickAdd(item); }, [onQuickAdd, item]);
  const handleDec = useCallback((e) => { e.stopPropagation(); onUpdateQty(simpleLineKey, -1); }, [onUpdateQty, simpleLineKey]);
  const handleOpenSheet = useCallback((e) => { e.stopPropagation(); onOpen(item.id); }, [onOpen, item.id]);

  // The right strip's base is a fixed 44px order bar, present on every orderable card so the
  // text column keeps ONE width down the whole list (only ~4/322 items carry a photo — the
  // no-image card is the common case). Exactly one state shows. "none" is only a display-only
  // venue's in-stock item: no order affordance, and (photo aside) no strip at all.
  let barState = "none";
  if (soldOut) barState = "soldout";                       // any venue — the bar replaces the old chip
  else if (canQuickAdd) barState = cartQty > 0 ? "stepper" : "add";
  else if (showCustomiseAdd) barState = "customise";       // customisable: Add opens the sheet
  const showStrip = hasImage || barState !== "none";

  return (
    <div
      data-testid={`pm-item-${item.id}`}
      role="button"
      tabIndex={0}
      aria-label={`View ${item.name}`}
      onClick={() => onOpen(item.id)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(item.id); } }}
      className={`relative rounded-xl border transition-all duration-200 overflow-hidden ${soldOut ? "opacity-45" : ""}`}
      style={{ background: branding.menu_card_color, borderColor: "rgba(255,255,255,0.07)", display: "flex", alignItems: "stretch", cursor: "pointer" }}
    >
      <div className="flex-1 min-w-0" style={{ padding: "14px 16px" }}>
        <div className="flex items-start gap-2">
            <DietDot type={item.item_type} />
            <div className="flex-1 min-w-0">
              {/* Name + badges */}
              <div className="flex items-start gap-2 flex-wrap">
                <span
                  style={{ color: branding.menu_text_color, fontFamily: "var(--font-editorial)", fontSize: "1.1rem", fontWeight: 600, lineHeight: 1.45 }}
                >
                  {item.name}
                </span>
                {item.is_featured && (
                  <span
                    className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full shrink-0"
                    style={{ background: "color-mix(in srgb, var(--pm-primary) 15%, transparent)", color: "var(--pm-primary)", border: "1px solid var(--pm-primary)" }}
                  >
                    <Star size={7} fill="currentColor" /> Bestseller
                  </span>
                )}
                {/* Must try / New follow the Bestseller pattern — color-mix against
                    var(--pm-primary) — so they inherit each venue's palette and stay legible
                    on light and dark cards alike. The old hardcoded purple/blue measured
                    2.00:1 and 1.44:1 on tea-villa-cafe's cream card (needs 4.5:1). */}
                {item.is_must_try && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full shrink-0"
                        style={{ background: "color-mix(in srgb, var(--pm-primary) 15%, transparent)", color: "var(--pm-primary)", border: "1px solid var(--pm-primary)" }}>
                    <Sparkles size={7} /> Must try
                  </span>
                )}
                {item.show_new_badge && (
                  <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full shrink-0"
                        style={{ background: "color-mix(in srgb, var(--pm-primary) 15%, transparent)", color: "var(--pm-primary)", border: "1px solid var(--pm-primary)" }}>
                    New
                  </span>
                )}
                {/* Shown only while the guest is still choosing an order type (AD-1): this dish is
                    limited to one of them. Amber, not the promo colour, so it reads as a limit. */}
                {item._orderTypeTag && (
                  <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full shrink-0"
                        style={{ background: "rgba(245,158,11,0.15)", color: "#D97706", border: "1px solid rgba(245,158,11,0.5)" }}>
                    {item._orderTypeTag}
                  </span>
                )}
              </div>
              <TagRow tagKeys={item.tags} catalog={catalog} />

              {/* Price — single or multi-variant (shared renderer; compact here). When
                  the item has addon groups, the "Customisable ›" cue rides the SAME row
                  after price/points (Part 3); it wraps below rather than truncating the
                  price at narrow widths, and the card never grows a line for it. Items
                  without addons render the price block exactly as before. */}
              {showCustomisableCue ? (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <PriceBlock item={item} branding={branding} loyaltyEnabled={loyaltyEnabled} stacked={false} />
                  <span
                    className="mt-1.5 inline-flex items-center gap-0.5 text-[11px]"
                    style={{ color: customisableColor, fontFamily: "var(--font-body)" }}
                  >
                    Customisable
                    <ChevronRight size={12} strokeWidth={2.5} style={{ marginLeft: -1 }} />
                  </span>
                </div>
              ) : (
                <PriceBlock item={item} branding={branding} loyaltyEnabled={loyaltyEnabled} stacked={false} />
              )}

              {item.description && (
                <p
                  className="mt-1"
                  style={{
                    fontSize: "0.78rem",
                    lineHeight: 1.45,
                    color: `${branding.menu_accent_text_color}99`,
                    fontFamily: "var(--font-body)",
                    // Clamp to 2 lines: the card's image strip is alignSelf:stretch, so an
                    // unclamped description drives card height AND stretches the photo with it
                    // (Cappuccino renders 96x158 today). The full text lives in the item sheet.
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                    overflow: "hidden",
                  }}
                >
                  {item.description}
                </p>
              )}

              {/* Per-item promo line */}
              {item.promo_line && (
                <p
                  className="mt-2 pl-2.5 text-[12px] italic"
                  style={{
                    borderLeft: `2px solid ${primary}`,
                    color: branding.menu_accent_text_color,
                    fontFamily: "var(--font-body)",
                    lineHeight: 1.45,
                  }}
                >
                  {item.promo_line}
                </p>
              )}
            </div>
          </div>

          {/* Bottle locker promo */}
          {!item.promo_line && item.show_bottle_promo && bottleMenuPromo === "savings_calculator" && (
            (() => {
              const s = calculateBottleSavings(item);
              return s ? (
                <div
                  className="mt-3 rounded-lg px-3 py-2 text-[12px] border"
                  style={{ background: `${primary}0d`, borderColor: `${primary}22`, color: branding.menu_accent_text_color }}
                >
                  <span className="font-semibold" style={{ color: primary }}>💡 Bottle over pegs saves you ₹{s.savings}</span>
                  <span className="ml-1">· Store the rest digitally — ask your server</span>
                </div>
              ) : (
                <div
                  className="mt-3 rounded-lg px-3 py-2 text-[12px] border"
                  style={{ background: `${primary}0d`, borderColor: `${primary}22`, color: branding.menu_accent_text_color }}
                >
                  <span className="font-semibold" style={{ color: primary }}>💡 Can't finish tonight?</span>
                  <span className="ml-1">Store your bottle here and enjoy it on your next visit.</span>
                </div>
              );
            })()
          )}
      </div>

      {/* Right strip — a constant 96px column on every orderable card (Part 5). The photo,
          when present, fills the space above a 44px order bar pinned to the base; with no photo
          the strip is empty above the same bar. Keeping it present either way is what holds the
          text column to ONE width down a 322-item list. The strip's radius + overflow:hidden
          hand the bar the card's bottom-right corner. */}
      {showStrip && (
        <div
          className="shrink-0"
          style={{
            width: 96,
            flexShrink: 0,
            alignSelf: "stretch",
            borderRadius: "0 0.75rem 0.75rem 0",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",             // no image → the bar still sits at the base
            minHeight: hasImage ? 120 : undefined,   // 44px bar + ≥76px of photo (was 96)
          }}
        >
          {hasImage && (
            <div style={{ position: "relative", flex: 1, minHeight: 0, overflow: "hidden" }}>
              {/* Absolutely positioned so the photo fills the flex-sized area above the bar
                  (object-fit: cover) instead of sizing to its own aspect ratio — otherwise a
                  tall portrait photo drives the card height and the point of the 2-line clamp
                  (bounded card height → near-constant image height) is lost. */}
              <img
                src={resolveImgUrl(item.image_url)}
                alt={item.name}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center top",
                  display: "block",
                }}
                loading="lazy"
              />
              {/* Keep the dark sold-out overlay on the photo itself; the bar below also flips
                  to Sold out. */}
              {soldOut && (
                <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
                  <span style={{ color: "#fff", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em", lineHeight: 1.3 }}>Item<br/>Sold Out</span>
                </div>
              )}
            </div>
          )}

          {/* Order bar — 44px min touch target, full strip width. Colours are venue tokens
              (primary fill, card-colour text) so the bar themes itself; one of three states. */}
          {(barState === "add" || barState === "customise") && (
            <button
              type="button"
              onClick={barState === "add" ? handleQuickAdd : handleOpenSheet}
              className="shrink-0"
              style={{
                height: 44, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
                background: "var(--pm-primary)", color: "var(--pm-card)",
                fontWeight: 700, fontSize: "0.8rem", fontFamily: "var(--font-body)",
              }}
            >
              <Plus size={14} /> Add
            </button>
          )}
          {barState === "stepper" && (
            <div
              className="shrink-0"
              style={{
                height: 44, width: "100%", display: "flex", alignItems: "stretch",
                background: "var(--pm-primary)", color: "var(--pm-card)",
              }}
            >
              {/* ~32px each control, count centred between them; each is the full 44px tall. */}
              <button type="button" aria-label="Decrease quantity" onClick={handleDec}
                style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--pm-card)" }}>
                <Minus size={15} />
              </button>
              <span style={{ minWidth: 24, textAlign: "center", alignSelf: "center", fontWeight: 700, fontSize: "0.85rem" }}>{cartQty}</span>
              <button type="button" aria-label="Increase quantity" onClick={handleInc}
                style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--pm-card)" }}>
                <Plus size={15} />
              </button>
            </div>
          )}
          {barState === "soldout" && (
            <div
              className="shrink-0"
              style={{
                height: 44, width: "100%", display: "flex", alignItems: "center", justifyContent: "center",
                background: "color-mix(in srgb, var(--pm-adaptive-text) 12%, transparent)",
                color: "var(--pm-adaptive-text)", fontWeight: 700, fontSize: "0.7rem",
                textTransform: "uppercase", letterSpacing: "0.04em", fontFamily: "var(--font-body)",
              }}
            >
              Sold out
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Category grid card ----------
function CategoryCard({ category, items, branding, onSelect, index, nameFontSize }) {
  const primary = branding.menu_primary_color;
  // Category image takes priority; fall back to first item image
  const catImg = resolveImgUrl(category.image_url);
  const itemImg = resolveImgUrl(items.find(it => it.image_url)?.image_url);
  const coverImg = catImg || itemImg;
  const total = items.length;
  const soldOutCount = items.filter(it => !it.is_available).length;
  const availableCount = total - soldOutCount;

  return (
    <button
      type="button"
      data-testid={`pm-cat-card-${category.id}`}
      onClick={() => onSelect(category.id)}
      className="relative w-full overflow-hidden rounded-2xl text-left transition-all duration-300 hover:shadow-xl active:scale-[0.97] tb-fade-up aspect-[4/3]"
      style={{
        background: coverImg
          ? "transparent"
          : `linear-gradient(135deg, var(--pm-card) 0%, ${primary}15 100%)`,
        border: coverImg ? "none" : `1px solid rgba(255,255,255,0.09)`,
        animationDelay: `${0.04 + index * 0.055}s`,
      }}
    >
      {/* ── Image path ── */}
      {coverImg && (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${coverImg})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
        </>
      )}

      {/* ── No-image premium fallback decorations ── */}
      {!coverImg && (
        <>
          {/* 3 px top accent line */}
          <div
            className="absolute top-0 inset-x-0 rounded-t-2xl"
            style={{ height: 3, background: primary }}
          />
          {/* Bottom-right decorative circle at 20% opacity */}
          <div
            className="absolute -bottom-4 -right-4 rounded-full pointer-events-none"
            style={{
              width: 72,
              height: 72,
              background: `${primary}33`,
            }}
          />
          {/* Second, slightly offset circle for depth */}
          <div
            className="absolute -bottom-2 -right-2 rounded-full pointer-events-none"
            style={{
              width: 44,
              height: 44,
              background: `${primary}1a`,
            }}
          />
        </>
      )}

      {/* ── Text content ── */}
      <div className="absolute inset-x-0 bottom-0 p-4">
        <h3
          className="line-clamp-2"
          style={{
            fontFamily: "var(--font-editorial)",
            fontSize: nameFontSize,
            fontWeight: 700,
            lineHeight: 1.25,
            color: coverImg ? "#fff" : "var(--pm-adaptive-text)",
          }}
        >
          {category.name}
        </h3>
        <div className="mt-0.5 flex items-center gap-2">
          <span
            className="text-[11px]"
            style={{ color: coverImg ? "rgba(255,255,255,0.6)" : "var(--pm-adaptive-text)", opacity: coverImg ? 1 : 0.6, fontFamily: "var(--font-body)" }}
          >
            {availableCount} {availableCount === 1 ? "item" : "items"}
            {soldOutCount > 0 && ` · ${soldOutCount} sold out`}
          </span>
        </div>
        {category.description && (
          <p
            className="mt-0.5 line-clamp-1"
            style={{
              fontFamily: "var(--font-body)",
              fontSize: "0.72rem",
              color: coverImg ? "rgba(255,255,255,0.5)" : "var(--pm-muted)",
            }}
          >
            {category.description}
          </p>
        )}
      </div>
    </button>
  );
}

// ---------- Main category grid card ----------
function MainCategoryCard({ mainCategory, subCatCount, branding, onSelect, index, nameFontSize }) {
  const primary = branding.menu_primary_color;
  const coverImg = resolveImgUrl(mainCategory.image_url);

  return (
    <button
      type="button"
      data-testid={`pm-mc-card-${mainCategory.id}`}
      onClick={() => onSelect(mainCategory.id)}
      className="relative w-full overflow-hidden rounded-2xl text-left transition-all duration-300 hover:shadow-xl active:scale-[0.97] tb-fade-up aspect-[4/3]"
      style={{
        background: coverImg
          ? "transparent"
          : `linear-gradient(135deg, var(--pm-card) 0%, ${primary}15 100%)`,
        border: coverImg ? "none" : `1px solid rgba(255,255,255,0.09)`,
        animationDelay: `${0.04 + index * 0.055}s`,
      }}
    >
      {coverImg && (
        <>
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${coverImg})` }} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
        </>
      )}
      {!coverImg && (
        <>
          <div className="absolute top-0 inset-x-0 rounded-t-2xl" style={{ height: 3, background: primary }} />
          <div className="absolute -bottom-4 -right-4 rounded-full pointer-events-none" style={{ width: 72, height: 72, background: `${primary}33` }} />
          <div className="absolute -bottom-2 -right-2 rounded-full pointer-events-none" style={{ width: 44, height: 44, background: `${primary}1a` }} />
        </>
      )}
      <div className="absolute inset-x-0 bottom-0 p-4">
        <h3
          className="line-clamp-2"
          style={{
            fontFamily: "var(--font-editorial)",
            fontSize: nameFontSize,
            fontWeight: 700,
            lineHeight: 1.25,
            color: coverImg ? "#fff" : "var(--pm-adaptive-text)",
          }}
        >
          {mainCategory.name}
        </h3>
        <div className="mt-0.5 flex items-center gap-2">
          <span
            className="text-[11px]"
            style={{
              color: coverImg ? "rgba(255,255,255,0.6)" : "var(--pm-adaptive-text)",
              opacity: coverImg ? 1 : 0.6,
              fontFamily: "var(--font-body)",
            }}
          >
            {subCatCount} {subCatCount === 1 ? "category" : "categories"}
          </span>
        </div>
      </div>
    </button>
  );
}


// ---------- Category detail drawer ----------
function CategoryDrawer({
  category, items, branding, isOpen, onClose,
  loyaltyEnabled, bottleLockerEnabled, bottleMenuPromo,
  globalFilters, onGlobalFilterChange,
  availableFilters,
  disclaimers, termsUrl,
  orderingQrEnabled, onOpenItem, onQuickAdd,
  cartQtyMap, onUpdateQty, cartCount, cartTotal, onViewCart,
  catalog, addonCatalog,
}) {
  const primary = branding.menu_primary_color;
  const drawerAdaptiveText = getLuminance(branding.menu_background_color || '#000000') > 0.45 ? '#1a1a1a' : '#ffffff';

  // Clear any active filter that no longer exists in availableFilters
  useEffect(() => {
    const invalid = globalFilters.filter(f => !availableFilters.includes(f));
    if (invalid.length > 0) {
      onGlobalFilterChange(globalFilters.filter(f => availableFilters.includes(f)));
    }
  }, [availableFilters]);

  const filteredItems = useMemo(() => {
    if (!items) return [];
    const visible = items.filter(it => {
      if (globalFilters.includes("veg") && it.item_type !== "veg" && it.item_type !== "vegan") return false;
      if (globalFilters.includes("nonveg") && it.item_type !== "nonveg") return false;
      if (globalFilters.includes("bestseller") && !it.is_featured) return false;
      if (globalFilters.includes("must_try") && !it.is_must_try) return false;
      if (globalFilters.includes("new") && !it.show_new_badge) return false;
      return true;
    });
    // Sort sold-out items to the bottom
    return [...visible].sort((a, b) => {
      if (a.is_available === b.is_available) return 0;
      return a.is_available ? -1 : 1;
    });
  }, [items, globalFilters]);

  return (
    <DrawerPrimitive.Root
      open={isOpen}
      onOpenChange={(open) => { if (!open) onClose(); }}
      shouldScaleBackground={false}
    >
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay
          className="fixed inset-0 z-50"
          style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(5px)" }}
        />
        <DrawerPrimitive.Content
          className="fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-3xl overflow-hidden"
          style={{
            maxHeight: "92dvh",
            background: branding.menu_background_color,
            "--pm-primary": primary,
            "--pm-primary-light": `${primary}1a`,
            "--pm-muted": branding.menu_accent_text_color,
            "--pm-adaptive-text": drawerAdaptiveText,
            "--menu-primary": primary,
          }}
        >
          {/* Drag handle */}
          <div
            className="mx-auto mt-3 mb-1 h-1 w-10 rounded-full shrink-0"
            style={{ background: "rgba(255,255,255,0.18)" }}
          />

          {/* Header */}
          <div
            className="sticky top-0 z-10 shrink-0 border-b px-4 pt-2 pb-3"
            style={{ borderColor: "rgba(255,255,255,0.08)", background: branding.menu_background_color }}
          >
            <div className="flex items-center justify-between mb-3">
              <DrawerPrimitive.Title
                className="font-medium text-xl"
                style={{ fontFamily: "var(--font-editorial)", color: "var(--pm-adaptive-text)" }}
              >
                {category?.name}
              </DrawerPrimitive.Title>
              <button
                onClick={onClose}
                aria-label="Close"
                className="flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-white/10"
                style={{ color: branding.menu_accent_text_color }}
              >
                <X size={16} />
              </button>
            </div>
            {/* Full category description as a subheading — untruncated here (the card
                only shows a one-line teaser), so allergen/serving notes aren't cut. */}
            {category?.description && (
              <p
                style={{
                  color: "var(--pm-muted)", fontSize: "0.8rem", lineHeight: 1.45,
                  margin: "-4px 0 10px", fontFamily: "var(--font-body)",
                }}
              >
                {category.description}
              </p>
            )}
            <FilterChips activeFilters={globalFilters} onFilterChange={onGlobalFilterChange} availableFilters={availableFilters} />
          </div>

          {/* Scrollable items. min-h-0 is REQUIRED: without it a flex-1 child keeps
              min-height:auto and refuses to shrink below its content, so under long
              content it overflowed the max-height flex column and the disclaimer
              (the next flex sibling) rendered over the last item instead of below the
              list. min-h-0 lets it shrink and scroll within its box. */}
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 pt-4 pb-6">
            {filteredItems.length === 0 ? (
              <div className="py-16 text-center">
                <p className="text-3xl mb-2">🔍</p>
                <p className="text-sm" style={{ color: branding.menu_accent_text_color }}>
                  No items match this filter.
                </p>
                <button
                  onClick={() => onGlobalFilterChange([])}
                  className="mt-3 text-xs underline underline-offset-4"
                  style={{ color: primary }}
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2.5">
                {filteredItems.map(it => (
                  <ItemCard
                    key={it.id}
                    item={it}
                    branding={branding}
                    bottleMenuPromo={bottleMenuPromo}
                    loyaltyEnabled={loyaltyEnabled}
                    orderingQrEnabled={orderingQrEnabled}
                    onOpen={onOpenItem}
                    onQuickAdd={onQuickAdd}
                    cartQty={cartQtyMap ? (cartQtyMap[cartLineKey(it.id, null, [])] || 0) : 0}
                    onUpdateQty={onUpdateQty}
                    catalog={catalog}
                    addonCatalog={addonCatalog}
                  />
                ))}
              </div>
            )}

            <div className="h-4" />
          </div>

          {/* Disclaimers + Loyalty T&Cs link — shown at the bottom of the drawer */}
          {(disclaimers?.length > 0 || termsUrl) && (
            <div
              style={{
                borderTop: "1px solid rgba(255,255,255,0.08)",
                padding: "8px 16px 12px",
                textAlign: "center",
                fontSize: "0.7rem",
                fontFamily: "var(--font-body)",
                flexShrink: 0,
              }}
            >
              {disclaimers?.length > 0 && (
                <ul style={{ listStyle: "none", padding: 0, margin: 0, opacity: 0.55, color: "var(--pm-adaptive-text)", marginBottom: termsUrl ? 4 : 0 }}>
                  {disclaimers.map((d, i) => <li key={i}>• {d}</li>)}
                </ul>
              )}
              {termsUrl && loyaltyEnabled && (
                <a
                  href={termsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: branding?.menu_primary_color || "#9D00FF",
                    textDecoration: "underline",
                    fontSize: "0.68rem",
                    opacity: 0.8,
                  }}
                >
                  Loyalty T&amp;Cs
                </a>
              )}
            </div>
          )}
          {/* Sticky cart bar */}
          {orderingQrEnabled && cartCount > 0 && (
            <div className="shrink-0 px-4 py-3 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
              <button
                type="button"
                onClick={onViewCart}
                className="w-full flex items-center justify-between rounded-xl px-4 py-3 font-semibold text-sm"
                style={{ background: branding.menu_primary_color, color: onPrimary(branding.menu_primary_color) }}
              >
                <div className="flex items-center gap-2">
                  <ShoppingCart size={16} />
                  <span>{cartCount} item{cartCount !== 1 ? "s" : ""}</span>
                </div>
                <span>View Cart · ₹{cartTotal.toFixed(0)}</span>
              </button>
            </div>
          )}
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  );
}

// ---------- Floating "Menu" button + all-categories sheet ----------
function FloatingMenuSheet({ categories, grouped, branding, onSelect, mainCategories }) {
  const [open, setOpen] = useState(false);
  const primary = branding.menu_primary_color;
  // Adaptive text for this drawer's card background — also feeds the themed overlay
  // scrollbar (index.css) via the tokens set on Content below.
  const drawerAdaptive = getLuminance(branding.menu_card_color || "#000000") > 0.45 ? "#1a1a1a" : "#ffffff";
  const total = categories.reduce((s, c) => s + (grouped[c.id]?.length || 0), 0);
  const hasMainCategories = mainCategories && mainCategories.length > 0;

  const handleSelect = (cid) => {
    setOpen(false);
    setTimeout(() => onSelect(cid), 120);
  };

  // Build grouped list: if main categories exist, show sub-categories grouped under their parent
  const renderCategoryList = () => {
    if (!hasMainCategories) {
      return categories.map((c) => {
        const count = grouped[c.id]?.length || 0;
        return (
          <button
            key={c.id}
            onClick={() => handleSelect(c.id)}
            className="w-full flex items-center gap-3 px-5 py-3.5 border-b text-left transition-colors hover:bg-white/5 active:bg-white/10"
            style={{ borderColor: "rgba(255,255,255,0.05)" }}
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold uppercase"
              style={{ background: `${primary}22`, color: primary }}>
              {c.name.charAt(0)}
            </span>
            <span className="flex-1 text-sm font-medium uppercase tracking-wide" style={{ color: branding.menu_text_color }}>{c.name}</span>
            <span className="text-sm tabular-nums" style={{ color: branding.menu_accent_text_color }}>{count}</span>
          </button>
        );
      });
    }

    // Grouped by main category
    const mcMap = {};
    mainCategories.forEach(mc => { mcMap[mc.id] = { mc, cats: [] }; });
    const unassigned = [];
    categories.forEach(c => {
      if (c.main_category_id && mcMap[c.main_category_id]) {
        mcMap[c.main_category_id].cats.push(c);
      } else {
        unassigned.push(c);
      }
    });

    const rows = [];
    mainCategories.forEach(mc => {
      const { cats } = mcMap[mc.id] || { cats: [] };
      if (cats.length === 0) return;
      rows.push(
        <div key={`mc-${mc.id}`}
          className="px-5 py-2 text-[10px] font-bold uppercase tracking-widest"
          style={{ color: primary, background: `${primary}0a` }}>
          {mc.name}
        </div>
      );
      cats.forEach(c => {
        const count = grouped[c.id]?.length || 0;
        rows.push(
          <button key={c.id} onClick={() => handleSelect(c.id)}
            className="w-full flex items-center gap-3 px-5 py-3 pl-8 border-b text-left transition-colors hover:bg-white/5 active:bg-white/10"
            style={{ borderColor: "rgba(255,255,255,0.05)" }}>
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold uppercase"
              style={{ background: `${primary}22`, color: primary }}>
              {c.name.charAt(0)}
            </span>
            <span className="flex-1 text-sm font-medium uppercase tracking-wide" style={{ color: branding.menu_text_color }}>{c.name}</span>
            <span className="text-sm tabular-nums" style={{ color: branding.menu_accent_text_color }}>{count}</span>
          </button>
        );
      });
    });
    // Unassigned categories
    if (unassigned.length > 0) {
      rows.push(
        <div key="mc-unassigned"
          className="px-5 py-2 text-[10px] font-bold uppercase tracking-widest"
          style={{ color: branding.menu_accent_text_color, background: "rgba(255,255,255,0.03)" }}>
          Other
        </div>
      );
      unassigned.forEach(c => {
        const count = grouped[c.id]?.length || 0;
        rows.push(
          <button key={c.id} onClick={() => handleSelect(c.id)}
            className="w-full flex items-center gap-3 px-5 py-3.5 border-b text-left transition-colors hover:bg-white/5 active:bg-white/10"
            style={{ borderColor: "rgba(255,255,255,0.05)" }}>
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold uppercase"
              style={{ background: `${primary}22`, color: primary }}>
              {c.name.charAt(0)}
            </span>
            <span className="flex-1 text-sm font-medium uppercase tracking-wide" style={{ color: branding.menu_text_color }}>{c.name}</span>
            <span className="text-sm tabular-nums" style={{ color: branding.menu_accent_text_color }}>{count}</span>
          </button>
        );
      });
    }
    return rows;
  };

  return (
    <DrawerPrimitive.Root open={open} onOpenChange={setOpen} shouldScaleBackground={false}>
      <DrawerPrimitive.Trigger asChild>
        <button
          data-testid="pm-floating-menu-btn"
          className="fixed bottom-6 right-4 z-40 flex items-center gap-2 rounded-full px-5 py-3 shadow-xl transition-all duration-200 hover:scale-105 active:scale-95 tb-fade-up"
          style={{ background: primary, color: onPrimary(primary), animationDelay: "0.45s" }}
          aria-label="Open category list"
        >
          <Menu size={15} />
          <span className="text-sm font-semibold">Menu</span>
        </button>
      </DrawerPrimitive.Trigger>

      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay
          className="fixed inset-0 z-50"
          style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
        />
        <DrawerPrimitive.Content
          className="fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-3xl overflow-hidden"
          style={{
            maxHeight: "80dvh", background: branding.menu_card_color,
            // Venue tokens so the re-enabled overlay scrollbar (index.css) is themed.
            "--pm-primary": primary,
            "--pm-muted": branding.menu_accent_text_color,
            "--pm-adaptive-text": drawerAdaptive,
          }}
        >
          <div
            className="mx-auto mt-3 mb-1 h-1 w-10 rounded-full shrink-0"
            style={{ background: "rgba(255,255,255,0.18)" }}
          />

          <div
            className="flex items-center justify-between px-5 py-3.5 border-b shrink-0"
            style={{ borderColor: "rgba(255,255,255,0.08)" }}
          >
            <div>
              <DrawerPrimitive.Title
                className="font-medium text-lg"
                style={{ fontFamily: "var(--font-editorial)", color: branding.menu_text_color }}
              >
                All Categories
              </DrawerPrimitive.Title>
              <p className="text-[11px]" style={{ color: branding.menu_accent_text_color, fontFamily: "var(--font-body)" }}>
                {total} items across {categories.length} {categories.length === 1 ? "category" : "categories"}
              </p>
            </div>
            <DrawerPrimitive.Close asChild>
              <button
                className="flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-white/10"
                style={{ color: branding.menu_accent_text_color }}
                aria-label="Close"
              >
                <X size={15} />
              </button>
            </DrawerPrimitive.Close>
          </div>

          <div data-pm-scroll="true" className="overflow-y-auto overscroll-contain flex-1">
            {renderCategoryList()}
            <div className="h-6" />
          </div>
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  );
}

// ---------- Registration overlay ----------
function RegistrationCard({ phone, venueName, branding, slug, onSuccess, onBack }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthDay, setBirthDay] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [fnErr, setFnErr] = useState("");

  const primary = branding.menu_primary_color;
  const bg = branding.menu_background_color;
  const cardBg = branding.menu_card_color;
  const text = branding.menu_text_color;
  const muted = branding.menu_accent_text_color;
  const inputStyle = {
    background: bg, color: text,
    border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10,
    padding: "10px 14px", fontSize: 16, width: "100%", outline: "none", minHeight: 44,
  };
  const selectStyle = { ...inputStyle, appearance: "none" };

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = firstName.trim();
    if (!trimmed) { setFnErr("First name is required."); return; }
    setFnErr("");
    setSubmitting(true);
    try {
      const { data: r } = await api.post("/public/menu/register-customer", {
        slug, phone,
        first_name: trimmed,
        last_name: lastName.trim() || undefined,
        birth_month: birthMonth ? parseInt(birthMonth, 10) : undefined,
        birth_day: birthDay ? parseInt(birthDay, 10) : undefined,
        birth_year: birthYear ? parseInt(birthYear, 10) : undefined,
      });
      onSuccess(r);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="pm-reg-card"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(8px)" }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-6 relative my-4"
        style={{ background: cardBg, color: text, boxShadow: `0 0 60px ${primary}33` }}
      >
        <button
          onClick={onBack}
          className="absolute top-4 left-4 flex items-center gap-1 text-xs opacity-60 hover:opacity-100 transition"
          style={{ color: muted }}
          data-testid="pm-reg-back"
        >
          <ChevronLeft size={14} /> Back
        </button>

        <div className="text-center mb-5 mt-2">
          <div className="text-3xl mb-2">🎉</div>
          <h2 className="text-xl font-black" style={{ color: text, fontFamily: "var(--font-editorial)", fontSize: "1.6rem" }}>
            Join {venueName}
          </h2>
          <p className="text-xs mt-1" style={{ color: muted }}>
            Earn loyalty points on every order — redeemable at the counter.
          </p>
          <div
            className="mt-2 inline-block text-xs font-bold px-3 py-1 rounded-full"
            style={{ background: `${primary}20`, color: primary }}
          >
            📱 +91 {phone}
          </div>
        </div>

        <form onSubmit={submit} className="flex flex-col gap-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider block mb-1.5" style={{ color: muted }}>
              First Name <span style={{ color: primary }}>*</span>
            </label>
            <input
              data-testid="pm-reg-firstname"
              value={firstName}
              onChange={e => { setFirstName(e.target.value); setFnErr(""); }}
              placeholder="e.g. Riya"
              style={inputStyle}
              autoFocus
            />
            {fnErr && <p className="text-xs mt-1" style={{ color: "#EF4444" }}>{fnErr}</p>}
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider block mb-1.5" style={{ color: muted }}>
              Last Name <span style={{ color: muted, fontWeight: 400, textTransform: "none" }}>(optional)</span>
            </label>
            <input
              data-testid="pm-reg-lastname"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              placeholder="e.g. Sharma"
              style={inputStyle}
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider block mb-1.5" style={{ color: muted }}>
              Birthday <span style={{ color: muted, fontWeight: 400, textTransform: "none" }}>(optional)</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              <select data-testid="pm-reg-birth-month" value={birthMonth} onChange={e => setBirthMonth(e.target.value)} style={selectStyle}>
                <option value="">Month</option>
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <select data-testid="pm-reg-birth-day" value={birthDay} onChange={e => setBirthDay(e.target.value)} style={selectStyle}>
                <option value="">Day</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <select data-testid="pm-reg-birth-year" value={birthYear} onChange={e => setBirthYear(e.target.value)} style={selectStyle}>
                <option value="">Year</option>
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
          <p className="text-[11px] leading-relaxed" style={{ color: muted }}>
            By joining, you agree to receive loyalty updates from{" "}
            <span style={{ color: text, fontWeight: 600 }}>{venueName}</span> on WhatsApp.
          </p>
          <button
            type="submit"
            disabled={submitting}
            data-testid="pm-reg-submit"
            className="w-full font-bold rounded-xl py-3 text-white transition disabled:opacity-60"
            style={{ background: primary, minHeight: 48 }}
          >
            {submitting ? "Joining…" : "Join & Start Earning 🏆"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------- Success screen ----------
function SuccessScreen({ customerName, venueName, firstMilestone, branding, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 3000); return () => clearTimeout(t); }, [onDone]);
  const primary = branding.menu_primary_color;
  return (
    <div
      data-testid="pm-success-screen"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.88)", backdropFilter: "blur(10px)" }}
    >
      <div
        className="w-full max-w-sm rounded-2xl p-8 text-center"
        style={{ background: branding.menu_card_color, color: branding.menu_text_color, boxShadow: `0 0 80px ${primary}44` }}
      >
        <div className="text-5xl mb-4">🎉</div>
        <h2
          className="text-3xl font-black mb-1"
          style={{ fontFamily: "var(--font-editorial)" }}
        >
          Welcome, {customerName}!
        </h2>
        <p className="text-sm mb-4" style={{ color: branding.menu_accent_text_color }}>
          You're now a loyalty member at <span style={{ fontWeight: 600 }}>{venueName}</span>.
        </p>
        {firstMilestone && (
          <div
            className="rounded-xl p-4 text-sm mb-4"
            style={{ background: `${primary}15`, border: `1px solid ${primary}40` }}
          >
            <div className="font-bold mb-0.5" style={{ color: primary }}>🏆 Your first goal</div>
            <div>
              Earn <span style={{ color: primary, fontWeight: 700 }}>{firstMilestone.milestone_points} pts</span> to unlock:{" "}
              <span style={{ fontWeight: 600 }}>{firstMilestone.reward_description}</span>
            </div>
          </div>
        )}
        <p className="text-xs" style={{ color: branding.menu_accent_text_color }}>Returning to menu…</p>
      </div>
    </div>
  );
}

// ---------- Loyalty bar (inline, non-sticky) ----------
function LoyaltyBar({
  branding, slug, phase, setPhase, phone, setPhone, phoneErr, setPhoneErr,
  checking, setChecking, ptsResult, setPtsResult, setPendingPhone, setRegResult, milestoneProgress,
  bottleLockerEnabled,
}) {
  const primary = branding.menu_primary_color;

  const handlePhoneChange = (e) => { setPhone(e.target.value); setPhoneErr(""); };

  const checkPoints = async (e) => {
    e.preventDefault();
    const pErr = validatePhone(phone);
    if (pErr) { setPhoneErr(pErr); return; }
    const clean = sanitizePhone(phone);
    setChecking(true);
    try {
      const { data: r } = await api.post("/public/menu/points-check", { slug, phone: clean });
      if (r.found === false) {
        setPendingPhone(clean);
        setPhase("register");
      } else {
        setPtsResult(r);
        setPhase("found");
      }
    } catch (err) { toast.error(formatError(err)); }
    finally { setChecking(false); }
  };

  const reset = () => {
    // Clear this bar's loyalty state. Bottle-locker state lives in the parent PublicMenu, not here —
    // LoyaltyBar only receives the loyalty setters as props — and the bottle flow deliberately
    // remembers phone/phase/result for the rest of the session (see its own close handler). The lines
    // that used to follow called setBottle* setters this component never had, so the clear button threw
    // "setBottlePhase is not defined" every time it ran (AI-3 sweep). Removed; nothing else cleared it.
    setPtsResult(null);
    setPendingPhone("");
    setPhone("");
    setPhoneErr("");
    setPhase("idle");
  };

  if (phase === "found" && ptsResult) {
    return (
      <div
        data-testid="pm-pts-result"
        className="mx-4 mb-4 rounded-xl border p-3.5"
        style={{ background: `${primary}0c`, borderColor: `${primary}25` }}
      >
        <div className="flex items-center justify-between gap-2">
          <div>
            <span className="text-sm font-semibold" style={{ color: branding.menu_text_color }}>
              Hey {ptsResult.customer.name?.split(" ")[0]}! 🏆{" "}
            </span>
            <span className="text-sm font-black" style={{ color: primary }}>
              {ptsResult.customer.points_balance} pts
            </span>
            <span className="text-xs ml-1" style={{ color: branding.menu_accent_text_color }}>
              = ₹{ptsResult.customer.points_balance} to redeem
            </span>
          </div>
          <button onClick={reset} className="text-xs underline underline-offset-2 shrink-0" style={{ color: branding.menu_accent_text_color }}>
            clear
          </button>
        </div>
        {ptsResult.has_redemption_rules && ptsResult.terms_url && (
          <div className="mt-2 text-[11px]" style={{ color: branding.menu_accent_text_color }}>
            Redemption rules apply —{" "}
            <a
              href={ptsResult.terms_url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2"
              style={{ color: primary }}
            >
              view terms
            </a>
          </div>
        )}
        {milestoneProgress && (
          <div className="mt-2.5">
            <div className="flex items-center justify-between text-[10px] mb-1" style={{ color: branding.menu_accent_text_color }}>
              <span>
                {milestoneProgress.remaining} pts to:{" "}
                <b style={{ color: "var(--pm-primary)" }}>{milestoneProgress.reward}</b>
              </span>
              <span style={{ color: primary }}>{milestoneProgress.pct}%</span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}>
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${milestoneProgress.pct}%`, background: primary }}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="mx-4 mb-4 rounded-xl border p-3.5"
      style={{ background: branding.menu_card_color, borderColor: "rgba(255,255,255,0.08)" }}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: branding.menu_accent_text_color, fontFamily: "var(--font-body)" }}>
        {bottleLockerEnabled ? "📱 Check your balance" : "📱 Check your points balance"}
      </p>
      <form onSubmit={checkPoints} className="flex gap-2">
        <input
          type="tel"
          value={phone}
          onChange={handlePhoneChange}
          placeholder="10-digit mobile number"
          data-testid="pm-pts-phone"
          maxLength={13}
          className="flex-1 min-w-0 border rounded-lg px-3 py-2 text-base"
          style={{
            background: branding.menu_background_color,
            borderColor: phoneErr ? "#EF4444" : "rgba(255,255,255,0.1)",
            color: branding.menu_text_color,
            outline: "none",
          }}
        />
        <button
          type="submit"
          disabled={checking || !phone.trim()}
          data-testid="pm-pts-check"
          className="disabled:opacity-60 text-white text-xs font-bold uppercase tracking-wider rounded-lg px-4 py-2 shrink-0 transition-opacity"
          style={{ background: primary, minHeight: 44 }}
        >
          {checking ? "…" : "Check"}
        </button>
      </form>
      {phoneErr && <p className="text-[11px] mt-1" style={{ color: "#EF4444" }}>{phoneErr}</p>}
    </div>
  );
}

// ---------- Main ----------
export default function PublicMenu() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  // Loyalty / phase state
  const [phone, setPhone] = useState("");
  const [phoneErr, setPhoneErr] = useState("");
  const [checking, setChecking] = useState(false);
  const [phase, setPhase] = useState("idle");
  const [pendingPhone, setPendingPhone] = useState("");
  const [ptsResult, setPtsResult] = useState(null);
  const [regResult, setRegResult] = useState(null);

  // Bottle locker check state
  const [bottlePhone, setBottlePhone] = useState("");
  const [bottlePhoneErr, setBottlePhoneErr] = useState("");
  const [bottlePhase, setBottlePhase] = useState("idle");
  // idle → loading → found | not_found | registered
  const [bottleResult, setBottleResult] = useState(null);
  const [bottleNameInput, setBottleNameInput] = useState("");
  const [bottleNameErr, setBottleNameErr] = useState("");
  const [bottleNameSaving, setBottleNameSaving] = useState(false);
  const [bottleSheetOpen, setBottleSheetOpen] = useState(false);

  // Cart state. A line: {key, item_id, name, variant_id, variant_label, addon_option_ids,
  // addons:[{id,name,price}], qty, unit_price, note}. `key` (see cartLineKey) is the
  // dedupe identity; the cart is persisted to localStorage per slug + context (effects below).
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  // The cart line currently being edited in the customisation sheet (null = adding new).
  const [editingLine, setEditingLine] = useState(null);
  // True when a cart restored from a previous session is old enough (1–4h) that we surface a
  // dismissible "items from earlier" line letting the guest review or clear it (see restore).
  const [staleCartPrompt, setStaleCartPrompt] = useState(false);
  // Which storage key the in-memory cart currently belongs to (see cartStorageKey). Lets the
  // restore effect reload when the context changes (table ⇄ room) instead of leaking one
  // context's cart into the other's key. null until the first restore runs.
  const cartKeyRef = useRef(null);
  // Set true right before a restore-driven setCart so the save effect skips that one change.
  // On a context switch the save effect fires in the same render as the restore (shared key
  // dep) while `cart` still holds the OLD context's items — saving then would write them under
  // the new key. Skipping also avoids pointlessly re-writing a cart we just loaded.
  const skipSaveRef = useRef(false);

  // Checkout form state
  const [orderName, setOrderName] = useState("");
  // Whether the current orderName was auto-filled by the phone lookup rather than typed by the
  // guest. A phone edit that drops below 10 digits clears an auto-filled name (it belonged to a
  // number no longer entered) but must never wipe a name the guest typed themselves.
  const orderNameAutoRef = useRef(false);
  const [orderPhone, setOrderPhone] = useState("");
  const [orderType, setOrderType] = useState("takeaway"); // "takeaway" | "dine_in" | "room"
  // Whether the guest has actually told us how they're ordering. On an unscoped "both" link we must
  // NOT guess (AD-1): until this is true the whole menu shows with single-type items marked, and
  // checkout is blocked, rather than silently filtering on a presumed "takeaway". A scoped link, a
  // room visit, or a single-mode venue sets it at once — the order type isn't in question there.
  const [typeChosen, setTypeChosen] = useState(false);
  const [orderTable, setOrderTable] = useState("");
  const [orderRoom, setOrderRoom] = useState("");
  const [orderVehicle, setOrderVehicle] = useState("");  // takeaway only, when the venue flag is on
  const [bill, setBill] = useState(null);                // server-priced bill for the checkout (Commit 1)
  const [pricing, setPricing] = useState(false);         // a price-preview request is in flight
  const [roomConfirmOpen, setRoomConfirmOpen] = useState(false);
  // QR dine-in → running bill (behind the venue flag). Service-point tables for the picker, plus a
  // confirmation before an order is added to a table that already has a running bill.
  const [attachSPs, setAttachSPs] = useState([]);
  const [checkConfirmOpen, setCheckConfirmOpen] = useState(false);
  const [orderNote, setOrderNote] = useState("");
  const [orderPlacing, setOrderPlacing] = useState(false);
  const [enrollLoyalty, setEnrollLoyalty] = useState(true); // pre-ticked
  const [orderResult, setOrderResult] = useState(null); // {order_number, total}
  const [payMethod, setPayMethod] = useState("online"); // takeaway choice: "cash" | "online"

  // Checkout phone-first lookup state
  const [checkoutLookupDone, setCheckoutLookupDone] = useState(false);
  const [checkoutLookupLoading, setCheckoutLookupLoading] = useState(false);
  const [checkoutFoundCustomer, setCheckoutFoundCustomer] = useState(null); // loyalty customer object if found
  const [checkoutReturningOrderer, setCheckoutReturningOrderer] = useState(false); // past order found but no loyalty

  // UI state
  const [search, setSearch] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [globalFilters, setGlobalFilters] = useState([]);

  // The URL is the source of truth for where the guest is, so the browser/hardware back
  // button unwinds one navigation layer at a time. Each layer is a search param:
  //   ?mc=<id>              main category
  //   ?mc=…&c=<id>          item-list drawer
  //   ?mc=…&c=…&item=<id>   item view
  const navigate = useNavigate();
  const activeMCId = searchParams.get("mc");
  const openCategoryId = searchParams.get("c");
  const openItemId = searchParams.get("item");

  // Closing any layer goes BACK through history when the guest navigated within the app,
  // so the URL, the on-screen state and the browser/hardware back button stay in lockstep.
  // On a fresh load or shared link there's no in-app entry to pop, so strip the layer's
  // own params in place (replace) — no dead forward entry, no dead back press. react-router
  // stores an incrementing idx in window.history.state (0 on first load); >0 means there
  // is in-app history behind us. (Verified against react-router 7.17.0.)
  const closeLayer = useCallback((...stripKeys) => {
    if (window.history.state?.idx > 0) {
      navigate(-1);
    } else {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        stripKeys.forEach(k => next.delete(k));
        return next;
      }, { replace: true });
    }
  }, [navigate, setSearchParams]);

  // Hero / sticky header state
  const heroRef = useRef(null);
  const menuSectionRef = useRef(null);
  const [heroVisible, setHeroVisible] = useState(true);

  // A collection link scopes the menu to one order type (?type=takeaway) — the shape Vinod shares for
  // pickup. We only honour a known order type; anything else is treated as an unscoped visit.
  const scopedTypeRaw = searchParams.get("type");
  const scopedType = ["takeaway", "dine_in", "room"].includes(scopedTypeRaw) ? scopedTypeRaw : null;

  // Load menu. A scoped link asks the server to drop anything that can't travel for that order type,
  // so the dish never reaches the page. The unscoped menu comes back whole (every item tagged with the
  // order types it's available for) and we filter it client-side, so the checkout toggle can refilter
  // with no refetch.
  useEffect(() => {
    api.get(`/public/menu/${slug}`, scopedType ? { params: { order_type: scopedType } } : undefined)
      .then(({ data }) => { setData(data); })
      .catch(e => setErr(
        e?.response?.status === 404
          ? "This menu isn't published yet."
          : "Could not load menu."
      ));
  }, [slug, scopedType]);

  // Public-menu route flag on <body> so index.css strips TabOn's dashboard
  // scrollbar/selection for this venue-branded page only. Removed on unmount.
  useEffect(() => {
    document.body.classList.add("pm-public");
    return () => document.body.classList.remove("pm-public");
  }, []);

  // Escape clears an active search from anywhere (the search header may be scrolled
  // off), returning to the previous menu view. No-op when nothing is searched.
  useEffect(() => {
    if (!search) return;
    const onKey = (e) => { if (e.key === "Escape") setSearch(""); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [search]);

  // IntersectionObserver: detect when hero section leaves the viewport
  useEffect(() => {
    if (!data || !heroRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setHeroVisible(entry.isIntersecting),
      { threshold: 0.05 }
    );
    observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, [data]);

  // Scroll hero arrow → smooth scroll to menu
  const scrollToMenu = useCallback(() => {
    menuSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const branding = { ...FALLBACK_BRANDING, ...(data?.branding || {}) };
  const venueFeatures = data?.venue?.features || {};
  const loyaltyEnabled = venueFeatures.loyalty_points !== false;
  const bottleLockerEnabled = venueFeatures.bottle_locker === true;
  const orderingEnabled    = venueFeatures.ordering === true;
  const orderingQrEnabled  = venueFeatures.ordering_qr === true;
  const orderingMode       = data?.venue?.ordering_mode || "takeaway";
  const orderingTableCount = data?.venue?.ordering_table_count || 10;
  const orderingOpen       = data?.venue?.ordering_open !== false;
  const vehicleEnabled     = data?.venue?.takeaway_vehicle_enabled === true;
  const roomServiceEnabled = data?.venue?.room_service_enabled === true;
  const roomServiceOpenNow = data?.venue?.room_service_open_now !== false;
  const roomServiceStart   = data?.venue?.room_service_start || "07:00";
  const roomServiceEnd     = data?.venue?.room_service_end || "23:00";
  const roomContext        = searchParams.get("context") === "room";
  const isRoomOrder        = roomContext && roomServiceEnabled;
  // When the venue attaches dine-in QR orders to a running bill, the table picker is driven by
  // service points (and sends service_point_id). Off → today's standalone-order flow, unchanged.
  const attachToCheck      = !isRoomOrder && orderType === "dine_in" && !!(data?.venue?.dine_in_qr_attaches_to_check);
  const spParam            = searchParams.get("sp") || "";
  // Storage key for the cart: the slug alone is not enough — a table order and a room order
  // (?context=room) settle on different bills, so they must keep independent carts. We key by
  // the raw context (defaulting the normal QR to "table") so any future context is isolated too.
  const cartContext        = searchParams.get("context") || "table";
  const cartStorageKey     = `${slug}:${cartContext}`;

  // The server price-preview effect lives further down, after availableCartLines is defined (AE-4) —
  // it prices only the payable lines, so it must come after they're derived.

  // Online payment (Phase 2) — takeaway only. Config comes from the menu payload.
  const paymentsEnabled    = data?.venue?.payments_enabled === true;
  const paymentMode        = data?.venue?.payment_mode || "sandbox";
  const takeawayMethods    = data?.venue?.payment_methods_takeaway || ["cash"];
  const takeawayOnline     = paymentsEnabled && takeawayMethods.includes("online");
  const takeawayHasChoice  = takeawayOnline && takeawayMethods.length > 1;
  const onlineAvailable    = !isRoomOrder && orderType === "takeaway" && takeawayOnline;
  // The method that will actually apply: online-only takeaway forces "online";
  // when a choice exists the customer's selection wins; everything else is cash.
  const effectivePayMethod = onlineAvailable
    ? (takeawayHasChoice ? payMethod : "online")
    : "cash";

  useEffect(() => {
    // Every branch but the last resolves the order type with no guesswork, so it counts as chosen.
    if (scopedType) {
      setOrderType(scopedType); setTypeChosen(true);   // a scoped link pins the type; the toggle is hidden
    } else if (isRoomOrder) {
      setOrderType("room"); setTypeChosen(true);
    } else if (orderingMode === "dine_in") {
      setOrderType("dine_in"); setTypeChosen(true);
    } else if (orderingMode === "takeaway") {
      setOrderType("takeaway"); setTypeChosen(true);
    } else {
      // orderingMode === "both", unscoped: the guest hasn't told us. Don't guess — show everything
      // and let the on-menu control (and the checkout toggle) record the choice.
      setTypeChosen(false);
    }
  }, [isRoomOrder, orderingMode, scopedType]);

  // Load service-point tables for the dine-in picker when the venue attaches QR orders to checks.
  useEffect(() => {
    if (!attachToCheck || !slug) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: sp } = await api.get(`/public/service-points/${slug}`);
        if (!cancelled) setAttachSPs(sp.service_points || []);
      } catch (_) { /* fall back to free text if unavailable */ }
    })();
    return () => { cancelled = true; };
  }, [attachToCheck, slug]);

  const bottleMenuPromo = data?.venue?.bottle_menu_promo || "none";

  // On an unscoped "both" menu the guest hasn't told us how they're ordering, so we don't filter at
  // all until they do (AD-1): show the whole menu and tag the single-type items. This is the one
  // state where a dine-in-only dish must stay visible to a seated guest instead of silently
  // vanishing. Scoped links and single-mode venues have this false and filter immediately.
  const choosingType = orderingMode === "both" && !isRoomOrder && !scopedType && !typeChosen;

  // Group items by category. Once the order type is settled, hide anything it can't carry (AC-1) plus
  // the collection-link scope the server already applied; while the guest is still choosing, keep
  // everything and attach a single-type tag for the card. available_for absent OR empty (no channel
  // info) → shown, since order time still enforces (AG). This memo feeds BOTH the category counts and
  // the category item list, so it alone drove both production symptoms ("0 items" and "No items match").
  const grouped = useMemo(() => {
    if (!data) return {};
    const m = {};
    data.categories.forEach(c => { m[c.id] = []; });
    data.items.forEach(it => {
      const avail = choosingType || availableForType(it.available_for, orderType);
      if (!m[it.category_id] || !avail) return;
      const tag = choosingType ? orderTypeTag(it.available_for) : null;
      m[it.category_id].push(tag ? { ...it, _orderTypeTag: tag } : it);
    });
    return m;
  }, [data, orderType, choosingType]);

  // Main categories (active only, sorted by display_order)
  const mainCategories = useMemo(() => {
    if (!data) return [];
    return (data.main_categories || []).filter(mc => mc.is_active);
  }, [data]);

  const hasMainCategories = mainCategories.length > 0;

  // Sub-category count per main category (for MainCategoryCard subtitle)
  const subCatCount = useMemo(() => {
    if (!data) return {};
    const counts = {};
    data.categories.forEach(c => {
      if (c.main_category_id) {
        counts[c.main_category_id] = (counts[c.main_category_id] || 0) + 1;
      }
    });
    return counts;
  }, [data]);

  // Categories visible in the current navigation level
  const visibleCategories = useMemo(() => {
    if (!data) return [];
    if (!hasMainCategories) return data.categories;  // flat mode
    if (!activeMCId) return [];                       // showing MC grid
    return data.categories.filter(c => c.main_category_id === activeMCId);
  }, [data, hasMainCategories, activeMCId]);

  // Categories under no RENDERED (active) main category — null parent, or a parent
  // that's inactive/absent. In grouped mode these match no activeMCId, so they'd be
  // unreachable; shown under "Other" below the grid (mirrors FloatingMenuSheet).
  const unassignedCategories = useMemo(() => {
    if (!data || !hasMainCategories) return [];
    const mcIds = new Set(mainCategories.map(mc => mc.id));
    return data.categories.filter(c => !c.main_category_id || !mcIds.has(c.main_category_id));
  }, [data, hasMainCategories, mainCategories]);

  const activeMC = hasMainCategories && activeMCId
    ? mainCategories.find(mc => mc.id === activeMCId) || null
    : null;

  // Navigation handlers
  const selectMainCategory = useCallback((mcid) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("mc", mcid);
      return next;
    }, { replace: false });
    menuSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [setSearchParams]);

  // Same close path as every overlay: pop history when we can (so the on-screen back
  // arrow and the hardware back button move together — they used to drift because this
  // pushed a new entry instead of popping), else strip the layer's params.
  const goBackToMainCategories = useCallback(() => closeLayer("mc", "c", "item"), [closeLayer]);

  // Compute which filter chips are relevant for this venue's menu
  const availableFilters = useMemo(() => {
    if (!data) return ["veg", "nonveg", "bestseller"];
    const filters = [];
    if (data.items.some(it => it.item_type === "veg" || it.item_type === "vegan")) filters.push("veg");
    if (data.items.some(it => it.item_type === "nonveg")) filters.push("nonveg");
    if (data.items.some(it => it.is_featured)) filters.push("bestseller");
    if (data.items.some(it => it.is_must_try)) filters.push("must_try");
    if (data.items.some(it => it.show_new_badge)) filters.push("new");
    return filters;
  }, [data]);

  // Search across all items
  const searchQuery = search.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!searchQuery || !data) return null;
    return data.items
      .filter(it => {
        // Search mirrors `grouped`: once the type is chosen, hide what it can't carry (AC-1) so a
        // result never offers an Add button the venue would refuse; while choosing, keep everything.
        // Absent or empty available_for is unrestricted (AG) — the same helper the menu uses.
        if (!choosingType && !availableForType(it.available_for, orderType)) return false;
        if (!it.name.toLowerCase().includes(searchQuery) && !(it.description || "").toLowerCase().includes(searchQuery)) return false;
        if (globalFilters.includes("veg") && it.item_type !== "veg" && it.item_type !== "vegan") return false;
        if (globalFilters.includes("nonveg") && it.item_type !== "nonveg") return false;
        if (globalFilters.includes("bestseller") && !it.is_featured) return false;
        if (globalFilters.includes("must_try") && !it.is_must_try) return false;
        if (globalFilters.includes("new") && !it.show_new_badge) return false;
        return true;
      })
      .map(it => {
        const cat = data.categories.find(c => c.id === it.category_id);
        const tag = choosingType ? orderTypeTag(it.available_for) : null;
        return { ...it, categoryName: cat?.name || "", ...(tag ? { _orderTypeTag: tag } : {}) };
      });
  }, [searchQuery, data, globalFilters, orderType, choosingType]);

  // Cart lines the current order type can't carry (AC-2). Flipping the order-type toggle can leave a
  // dish in the cart that the new type doesn't serve (and a cart restored from an earlier visit can
  // too); rather than let it fail at order time, we name each one in the checkout sheet and block the
  // order until the cart holds only what can be served. Same available_for signal the menu is filtered
  // by. An item dropped from the menu entirely is reconciled away in the restore effect, not here.
  const cartItemsById = useMemo(() => {
    const m = {};
    for (const it of (data?.items || [])) m[it.id] = it;
    return m;
  }, [data]);
  const unavailableCartLines = useMemo(() => {
    if (choosingType) return [];   // no order type chosen yet (AD-1) — nothing is ruled out
    return cart.filter(line => {
      const item = cartItemsById[line.item_id];
      // Absent or empty available_for is unrestricted (AG): never flag a line the menu would show.
      return item && !availableForType(item.available_for, orderType);
    });
  }, [cart, cartItemsById, orderType, choosingType]);
  // Line-key lookup so the cart sheet (AE-3) can mark the exact lines the checkout panel blocks on.
  const unavailableKeys = useMemo(() => new Set(unavailableCartLines.map(l => l.key)), [unavailableCartLines]);
  // Only the payable lines are priced and totalled (AE-4): a blocked line is listed separately and
  // never added in, so the checkout never shows a total the guest can't actually pay.
  const availableCartLines = useMemo(() => cart.filter(c => !unavailableKeys.has(c.key)), [cart, unavailableKeys]);
  const availableTotal = useMemo(() => availableCartLines.reduce((s, c) => s + c.unit_price * c.qty, 0), [availableCartLines]);

  // Price the cart on the SERVER (Commit 1) so the checkout shows exactly what the customer is
  // charged — tax, charges and round-off, never the frontend's own arithmetic. Re-prices whenever the
  // payable cart or order type changes; `pricing` keeps the button from showing a stale figure. Only
  // the lines the order type can carry are priced (AE-4) — a blocked line isn't payable, so it must
  // not enter the total; no payable lines → no bill (the summary then shows the blocked lines alone).
  useEffect(() => {
    if (!checkoutOpen || availableCartLines.length === 0) { setBill(null); setPricing(false); return; }
    let cancelled = false;
    setPricing(true);
    const items = availableCartLines.map((c) => ({
      item_id: c.item_id,
      variant_id: c.variant_id || undefined,
      addon_option_ids: (c.addon_option_ids && c.addon_option_ids.length) ? c.addon_option_ids : undefined,
      qty: c.qty,
    }));
    const t = setTimeout(async () => {
      try {
        const { data: res } = await api.post("/public/orders/price-preview", {
          slug,
          order_type: isRoomOrder ? "room" : orderType,
          service_point_id: attachToCheck ? (orderTable || undefined) : undefined,
          items,
        });
        if (!cancelled) setBill(res.bill);
      } catch (_) {
        if (!cancelled) setBill(null);   // leave the button disabled rather than show a wrong total
      } finally {
        if (!cancelled) setPricing(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [checkoutOpen, availableCartLines, slug, orderType, isRoomOrder, attachToCheck, orderTable]);

  // Registration handlers
  const handleRegistered = (result) => { setRegResult(result); setPhase("success"); };
  const handleSuccessDone = () => {
    if (regResult) {
      setPtsResult({
        found: true,
        customer: regResult.customer,
        next_milestone: regResult.first_milestone
          ? {
              milestone_points: regResult.first_milestone.milestone_points,
              reward_description: regResult.first_milestone.reward_description,
              remaining: regResult.first_milestone.milestone_points,
            }
          : null,
        venue_name: regResult.venue_name,
      });
    }
    setPhase("found");
    setRegResult(null);
  };

  // Milestone progress bar
  const milestoneProgress = useMemo(() => {
    if (!ptsResult?.next_milestone || !ptsResult?.customer) return null;
    const pts = ptsResult.customer.points_balance || 0;
    const target = ptsResult.next_milestone.milestone_points;
    const remaining = ptsResult.next_milestone.remaining;
    const pct = target > 0 ? Math.min(100, Math.round(((target - remaining) / target) * 100)) : 0;
    return { pts, target, remaining, pct, reward: ptsResult.next_milestone.reward_description };
  }, [ptsResult]);

  // Auto-trigger bottle check when loyalty result loads and bottle locker is enabled
  useEffect(() => {
    if (
      ptsResult?.customer?.phone &&
      bottleLockerEnabled &&
      bottlePhase === "idle"
    ) {
      checkBottle();
    }
  }, [ptsResult]);

  const bottleSheetClose = () => {
    setBottleSheetOpen(false);
    // Do not reset bottlePhone — remember it for this session
    // Do not reset bottlePhase or bottleResult — customer sees their result when reopening
  };

  const checkBottle = async (e) => {
    e?.preventDefault();
    // If phone is already known from loyalty check session, use it
    const phoneToUse = ptsResult?.customer?.phone || bottlePhone;
    const clean = phoneToUse.replace(/\D/g, "").replace(/^(\+91|91|0)/, "").trim();
    if (clean.length !== 10) {
      setBottlePhoneErr("Enter a valid 10-digit mobile number");
      return;
    }
    setBottlePhoneErr("");
    setBottlePhase("loading");
    try {
      const { data } = await api.post("/public/menu/bottle-check", {
        slug,
        phone: clean,
      });
      setBottleResult(data);
      setBottlePhase(data.found ? "found" : "not_found");
      // If not found, pre-fill phone for registration
      if (!data.found) setBottlePhone(clean);
    } catch (err) {
      setBottlePhoneErr("Something went wrong. Please try again.");
      setBottlePhase("idle");
    }
  };

  const registerBottle = async () => {
    if (!bottleNameInput.trim()) {
      setBottleNameErr("Please enter your name");
      return;
    }
    setBottleNameErr("");
    setBottleNameSaving(true);
    try {
      const { data } = await api.post("/public/menu/bottle-register", {
        slug,
        phone: bottlePhone,
        first_name: bottleNameInput.trim(),
      });
      setBottleResult(data);
      setBottlePhase("registered");
    } catch (err) {
      setBottleNameErr("Something went wrong. Please try again.");
    } finally {
      setBottleNameSaving(false);
    }
  };

  // Category drawer — keep lastOpenCategoryId so content stays mounted through Vaul's
  // close animation (openCategoryId now comes from ?c and clears the instant it's popped).
  const [lastOpenCategoryId, setLastOpenCategoryId] = useState(null);

  // Opening a layer PUSHES its param so it earns a history entry; merge onto the existing
  // params (never rebuild) so ?context=room and anything else survive. Filters are reset
  // by an effect keyed on ?c (below), so it fires whether the guest tapped in or pressed
  // back — not here.
  const openCategory = useCallback((cid) => {
    setLastOpenCategoryId(cid);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("c", cid);
      return next;
    }, { replace: false });
  }, [setSearchParams]);
  const openItem = useCallback((itemId) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("item", itemId);   // leaves ?c intact, so closing the item reopens the drawer here
      return next;
    }, { replace: false });
  }, [setSearchParams]);

  const closeItemView = useCallback(() => closeLayer("item"), [closeLayer]);
  // When an item is open the drawer is only closed programmatically BEHIND it (its isOpen
  // gates on !openItemId). Vaul's onOpenChange(false) that fires from that is not a user
  // dismiss, so ignore it — otherwise opening an item would immediately pop the drawer.
  const closeCategory = useCallback(() => {
    if (openItemId) return;
    closeLayer("c");
  }, [closeLayer, openItemId]);

  const displayCategoryId = openCategoryId || lastOpenCategoryId;
  const openCategoryData = data?.categories.find(c => c.id === displayCategoryId) || null;
  const openItemData = data?.items.find(i => i.id === openItemId) || null;

  // Reset filters when the open category changes — keyed on ?c so it fires the same
  // whether the guest tapped a category or arrived by pressing back/forward.
  useEffect(() => {
    setGlobalFilters([]);
  }, [openCategoryId]);

  // Validate the URL against the loaded menu once it resolves. Strip params that no longer
  // resolve (a stale shared link, a POS-deleted item) with { replace: true } so the guest
  // lands at the nearest valid level instead of a blank sheet and the bad entry doesn't
  // linger in history. Where ?c is valid but ?mc is missing, derive ?mc from its parent.
  useEffect(() => {
    if (!data) return;
    const mc = searchParams.get("mc");
    const c = searchParams.get("c");
    const item = searchParams.get("item");
    if (!mc && !c && !item) return;

    const next = new URLSearchParams(searchParams);
    let changed = false;

    if (item && !data.items.some(i => i.id === item)) { next.delete("item"); changed = true; }

    const catObj = c ? data.categories.find(x => x.id === c) : null;
    if (c && !catObj) { next.delete("c"); next.delete("item"); changed = true; }

    if (hasMainCategories) {
      const mcNow = next.get("mc");
      const parentMc = catObj?.main_category_id;
      if (catObj && !mcNow && parentMc) {
        next.set("mc", parentMc); changed = true;                 // derive a missing parent
      } else if (mcNow && !mainCategories.some(m => m.id === mcNow)) {
        if (parentMc && mainCategories.some(m => m.id === parentMc)) { next.set("mc", parentMc); }
        else { next.delete("mc"); }
        changed = true;                                            // replace/drop a stale mc
      }
    } else if (mc) {
      next.delete("mc"); changed = true;                          // flat menu: mc is meaningless
    }

    if (changed) setSearchParams(next, { replace: true });
  }, [data, searchParams, hasMainCategories, mainCategories, setSearchParams]);

  // Load the cart for the current storage key once the menu is available, dropping any line
  // whose item or variant no longer resolves (a menu edit / POS delete) and re-pricing the
  // base — so a phone lock or reload doesn't lose an order in progress, and a stale line
  // can't slip through. Re-runs when the key changes (table ⇄ room) to swap in that context's
  // cart; cartKeyRef guards against re-importing storage on an ordinary cart change.
  useEffect(() => {
    if (!data || cartKeyRef.current === cartStorageKey) return;
    cartKeyRef.current = cartStorageKey;
    const { lines, savedAt } = loadStoredCart(cartStorageKey);
    // Age from the last save. A missing savedAt (pre-timestamp cart, or corrupt) is treated as
    // infinitely old so it's discarded rather than restored with an unknown age.
    const ageMs = savedAt ? Date.now() - savedAt : Infinity;
    // Set even when empty: the in-memory cart must always match the active context's key, so
    // switching context never leaves the previous context's items behind (they'd be re-saved
    // under the wrong key by the effect below). skipSaveRef stops each setCart here from being
    // written straight back (which would also refresh the timestamp and defeat the ageing).
    skipSaveRef.current = true;
    if (ageMs > CART_MAX_AGE_MS) {
      saveStoredCart(cartStorageKey, []);  // >4h (or unknown age): discard the stale entry too
      setStaleCartPrompt(false);
      setCart([]);
      return;
    }
    const byId = {};
    for (const it of (data.items || [])) byId[it.id] = it;
    const reconciled = lines.map(l => reconcileCartLine(l, byId)).filter(Boolean);
    // 1–4h old: restore, but flag it so the guest is nudged to review or clear rather than
    // silently ordering last visit's items. Under 1h restores with no prompt.
    setStaleCartPrompt(reconciled.length > 0 && ageMs > CART_REVIEW_AGE_MS);
    setCart(reconciled);
  }, [data, cartStorageKey]);

  // Persist the cart on every change, under the key it currently belongs to. Skip the one
  // change made by a restore (skipSaveRef), and skip until the key is aligned so we never
  // write the old context's cart under a newly-changed key.
  useEffect(() => {
    if (skipSaveRef.current) { skipSaveRef.current = false; return; }
    if (cartKeyRef.current !== cartStorageKey) return;
    saveStoredCart(cartStorageKey, cart);
  }, [cart, cartStorageKey]);

  // Clear the "editing" marker whenever the customisation sheet closes (any path — X,
  // back, Escape), so a later fresh open of the same item never re-enters edit mode.
  useEffect(() => {
    if (!openItemId) setEditingLine(null);
  }, [openItemId]);

  // --- Error / loading states ---
  if (err) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-6"
        style={{ background: FALLBACK_BRANDING.menu_background_color, color: FALLBACK_BRANDING.menu_text_color }}
      >
        <div className="text-center">
          <div className="text-3xl font-black mb-2">Menu unavailable</div>
          <div className="text-sm" style={{ color: FALLBACK_BRANDING.menu_accent_text_color }}>{err}</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: FALLBACK_BRANDING.menu_background_color, color: FALLBACK_BRANDING.menu_accent_text_color }}
      >
        <div className="text-sm">Loading menu…</div>
      </div>
    );
  }

  const primary = branding.menu_primary_color;
  const secondary = branding.menu_secondary_color;
  const bgHex = branding.menu_background_color || '#000000';
  const isLightBg = getLuminance(bgHex) > 0.45;
  const adaptiveText = isLightBg ? '#1a1a1a' : '#ffffff';

  // CSS custom properties for this menu instance
  const pageStyle = {
    background: branding.menu_background_color,
    color: branding.menu_text_color,
    "--pm-primary": primary,
    "--pm-primary-light": `${primary}1a`,
    "--pm-secondary": secondary,
    "--pm-bg": branding.menu_background_color,
    "--pm-card": branding.menu_card_color,
    "--pm-text": branding.menu_text_color,
    "--pm-muted": branding.menu_accent_text_color,
    "--pm-adaptive-text": adaptiveText,
    "--menu-primary": primary,
  };


  // One name font-size per rendered grid (FIX B). Each list sizes independently:
  // the main-category grid from mainCategories, the sub-category grid from
  // visibleCategories (already scoped to the active main category, so a different
  // main category's sub-categories size on their own).
  const mainCategoryNameSize = nameSizeFor(mainCategories);
  const subCategoryNameSize = nameSizeFor(visibleCategories);
  const unassignedNameSize = nameSizeFor(unassignedCategories);

  // A search is active whenever the box has content (used to keep the search header
  // pinned so the customer is never trapped in results with no visible way out).
  const searchActive = search.trim() !== "";

  // Whole-venue "Pure vegetarian kitchen" statement — rendered as a line of hero
  // text (see below), NOT a pill, so it doesn't read as a tappable filter chip. The
  // veg symbol stays regulatory green; per-item veg/non-veg dots are unaffected.
  const pureVeg = data?.venue?.menu_pure_veg === true;

  const cartTotal = cart.reduce((s, it) => s + it.unit_price * it.qty, 0);
  const cartCount = cart.reduce((s, it) => s + it.qty, 0);

  // Apply a customisation-sheet selection to the cart. Adding: merge onto an identical
  // line (same key) by incrementing qty, else append. Editing: replace in place when the
  // key is unchanged (keeps position), else drop the original and merge/append under the
  // new key. The sheet already priced the line server-parity; the server re-prices anyway.
  const applySelection = (sel) => {
    const variantId = sel.variant?.id || null;
    const ids = [...(sel.addonOptionIds || [])];
    const key = cartLineKey(sel.item.id, variantId, ids);
    const line = {
      key,
      item_id: sel.item.id,
      name: sel.item.name,
      variant_id: variantId,
      variant_label: sel.variant?.label || null,
      addon_option_ids: ids,
      addons: sel.addons || [],
      qty: Math.max(1, sel.qty || 1),
      unit_price: sel.unitPrice,
      note: sel.note || "",
    };
    setCart(prev => {
      const merge = (arr) => {
        const i = arr.findIndex(c => c.key === key);
        if (i >= 0) { const n = [...arr]; n[i] = { ...n[i], qty: n[i].qty + line.qty }; return n; }
        return [...arr, line];
      };
      if (editingLine) {
        if (key === editingLine.key) return prev.map(c => c.key === key ? { ...line } : c);
        return merge(prev.filter(c => c.key !== editingLine.key));
      }
      return merge(prev);
    });
  };

  // Card quick-add for a plain item (no variant / no add-ons).
  const quickAdd = (item) => applySelection({
    item, variant: null, addonOptionIds: [], addons: [],
    unitPrice: Number(item.price) || 0, qty: 1,
  });

  // Open the sheet on an existing line to edit its choices; save replaces the line.
  const editCartLine = (line) => {
    setEditingLine(line);
    setCartOpen(false);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("item", line.item_id);
      return next;
    }, { replace: false });
  };

  const removeFromCart = (key) => {
    setCart(prev => prev.filter(c => c.key !== key));
  };

  const updateQty = (key, delta) => {
    setCart(prev => prev
      .map(c => c.key === key ? { ...c, qty: Math.max(0, c.qty + delta) } : c)
      .filter(c => c.qty > 0)
    );
  };

  const clearCart = () => setCart([]);

  // Phone-first lookup — fires when 10 digits entered in checkout phone field
  const lookupCheckoutPhone = async (cleanPhone) => {
    setCheckoutLookupLoading(true);
    setCheckoutLookupDone(false);
    setCheckoutFoundCustomer(null);
    setCheckoutReturningOrderer(false);
    try {
      const { data: r } = await api.post("/public/menu/points-check", {
        slug,
        phone: cleanPhone,
      });
      if (r.found && r.customer) {
        // Existing loyalty member
        setCheckoutFoundCustomer(r.customer);
        setCheckoutReturningOrderer(true);
        if (!orderName && r.first_name) { setOrderName(r.first_name); orderNameAutoRef.current = true; }
      } else if (r.returning_orderer && r.first_name) {
        // Has ordered here before but not a loyalty member
        setCheckoutFoundCustomer(null);
        setCheckoutReturningOrderer(true);
        if (!orderName) { setOrderName(r.first_name); orderNameAutoRef.current = true; }
      } else {
        // Brand new customer
        setCheckoutFoundCustomer(null);
        setCheckoutReturningOrderer(false);
      }
    } catch {
      // Silent fail — customer fills manually
      setCheckoutFoundCustomer(null);
      setCheckoutReturningOrderer(false);
    } finally {
      setCheckoutLookupDone(true);
      setCheckoutLookupLoading(false);
    }
  };

  // Cashfree JS SDK — provider-specific by nature (this is the checkout hand-off).
  // Loaded on demand; mode comes from the menu payload, never hardcoded.
  const _loadCashfreeSdk = () => new Promise((resolve, reject) => {
    if (window.Cashfree) return resolve(window.Cashfree);
    const s = document.createElement("script");
    s.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
    s.async = true;
    s.onload = () => (window.Cashfree ? resolve(window.Cashfree) : reject(new Error("SDK unavailable")));
    s.onerror = () => reject(new Error("Could not load the payment SDK"));
    document.body.appendChild(s);
  });

  const _startOnlinePayment = async (orderId) => {
    // Get a session, then hand off to the SDK. redirectTarget "_self" navigates to
    // the payment page and returns to /order/:id, which confirms via polling. The
    // browser NEVER marks an order paid — only the webhook / verified poller may.
    const { data: sess } = await api.post(`/public/orders/${orderId}/payment-session`, {});
    const Cashfree = await _loadCashfreeSdk();
    const cf = Cashfree({ mode: paymentMode });
    cf.checkout({ paymentSessionId: sess.payment_session_id, redirectTarget: "_self" });
  };

  const _submitOrder = async () => {
    const cleanPhone = orderPhone.replace(/\D/g, "");
    setOrderPlacing(true);
    try {
      const { data: res } = await api.post("/public/orders", {
        slug,
        customer_name: orderName.trim(),
        customer_phone: cleanPhone,
        order_type: isRoomOrder ? "room" : orderType,
        room_number: isRoomOrder ? orderRoom.trim() : undefined,
        // table_number is a string now (real tables are named, e.g. "Terrace 3"). When attaching
        // to a running bill, send service_point_id instead and let the server derive table_number.
        table_number: (!isRoomOrder && orderType === "dine_in" && !attachToCheck) ? (String(orderTable).trim() || undefined) : undefined,
        service_point_id: attachToCheck ? (orderTable || undefined) : undefined,
        vehicle_number: (!isRoomOrder && orderType === "takeaway" && vehicleEnabled && orderVehicle.trim())
          ? orderVehicle.trim() : undefined,
        special_note: orderNote.trim() || undefined,
        enroll_loyalty: enrollLoyalty,
        payment_mode: effectivePayMethod, // "cash" | "online" — drives confirmation timing
        items: cart.map(c => ({
          item_id: c.item_id,
          variant_id: c.variant_id || undefined,
          addon_option_ids: (c.addon_option_ids && c.addon_option_ids.length) ? c.addon_option_ids : undefined,
          qty: c.qty,
          note: c.note || undefined,
        })),
      });
      clearCart();
      setOrderNote("");
      // Attached to a running bill — no standalone order number; show the bill instead.
      if (res.attached_to_check) {
        setCheckConfirmOpen(false);
        setOrderResult({ attached: true, check_number: res.check_number, total: res.total });
        return;
      }
      if (effectivePayMethod === "online" && res.order_id) {
        try {
          await _startOnlinePayment(res.order_id);
          return; // page is navigating to the payment page
        } catch (_pe) {
          toast.error("Couldn't open the payment page. Your order is saved — please try again.");
        }
      }
      setOrderResult({ order_number: res.order_number, total: res.total });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to place order. Please try again.");
    } finally {
      setOrderPlacing(false);
      setRoomConfirmOpen(false);
      setCheckConfirmOpen(false);
    }
  };

  const placeOrder = async () => {
    const cleanPhone = orderPhone.replace(/\D/g, "");
    if (cleanPhone.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    if (!orderName.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (isRoomOrder && !orderRoom.trim()) {
      toast.error("Please enter your room number");
      return;
    }
    if (!isRoomOrder && orderType === "dine_in" && !orderTable) {
      toast.error("Please select your table number");
      return;
    }
    if (cart.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    if (choosingType) {
      toast.error("Please choose how you're ordering first");
      return;
    }
    if (unavailableCartLines.length > 0) {
      toast.error(`Remove what isn't available for ${orderTypeLabel(orderType)} first`);
      return;
    }
    if (isRoomOrder) {
      setRoomConfirmOpen(true);
      return;
    }
    // Attaching to a running bill: confirm before adding to a table that already has one, so a
    // mis-tap can't put this order on a stranger's bill.
    if (attachToCheck) {
      const sp = attachSPs.find(s => s.id === orderTable);
      if (sp && sp.running) {
        setCheckConfirmOpen(true);
        return;
      }
    }
    await _submitOrder();
  };

  return (
    <div data-testid="pm-root" className="min-h-screen" style={{ ...pageStyle, fontFamily: "var(--font-body)" }}>

      {/* === Overlays === */}
      {phase === "register" && (
        <RegistrationCard
          phone={pendingPhone}
          venueName={data.venue.name}
          branding={branding}
          slug={slug}
          onSuccess={handleRegistered}
          onBack={() => { setPendingPhone(""); setPhone(""); setPhoneErr(""); setPhase("idle"); }}
        />
      )}
      {phase === "success" && regResult && (
        <SuccessScreen
          customerName={regResult.customer?.name?.split(" ")[0] || "there"}
          venueName={regResult.venue_name || data.venue.name}
          firstMilestone={regResult.first_milestone}
          branding={branding}
          onDone={handleSuccessDone}
        />
      )}

      {/* ============================================================
          SECTION 1 — HERO (100vh)
      ============================================================ */}
      <section
        ref={heroRef}
        className="pm-hero relative w-full overflow-hidden"
      >
        {data.venue.promo_cover_url ? (
          /* ── Promo cover: full-bleed, no overlays ── */
          <>
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${data.venue.promo_cover_url})` }}
            />
            {/* Bottom gradient for chevron legibility */}
            <div className="absolute inset-x-0 bottom-0 pointer-events-none" style={{ height: "12%", background: "linear-gradient(to top, rgba(0,0,0,0.35), transparent)" }} />
            {/* Scroll chevron — bottom-center */}
            <button
              onClick={scrollToMenu}
              className="absolute left-1/2 -translate-x-1/2 p-3"
              style={{ bottom: 16 }}
              aria-label="Scroll to menu"
            >
              <ChevronDown
                size={22}
                className="pm-bounce"
                style={{ color: "rgba(255,255,255,0.9)" }}
              />
            </button>
          </>
        ) : data.venue.cover_url ? (
          /* ── Standard cover image path ── */
          <>
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${data.venue.cover_url})` }}
            />
            <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.50)" }} />
          </>
        ) : (
          /* ── Premium gradient fallback ── */
          <>
            <div
              className="absolute inset-0"
              style={{
                background: `
                  radial-gradient(ellipse at 50% 100%, ${primary}40 0%, ${branding.menu_background_color} 65%),
                  radial-gradient(ellipse 80% 60% at 15% 10%, ${secondary}99, transparent 55%),
                  ${branding.menu_background_color}
                `,
              }}
            />
            {/* Subtle grid overlay */}
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `
                  linear-gradient(${primary}0d 1px, transparent 1px),
                  linear-gradient(90deg, ${primary}0d 1px, transparent 1px)
                `,
                backgroundSize: "48px 48px",
              }}
            />
          </>
        )}

        {/* Logo + bottom content — hidden in promo mode */}
        {!data.venue.promo_cover_url && (
          <>
            {/* Logo — centered, top 20% */}
            <div
              className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center"
              style={{ top: "20%", transform: "translate(-50%, -50%)" }}
            >
              {branding.menu_logo_url ? (
                <img
                  src={resolveImgUrl(branding.menu_logo_url)}
                  alt={`${data.venue.name} logo`}
                  data-testid="pm-logo"
                  style={{
                    maxHeight: 110,
                    maxWidth: 220,
                    objectFit: "contain",
                    filter: "drop-shadow(0 2px 12px rgba(0,0,0,0.6))"
                  }}
                />
              ) : (
                <div
                  className="flex items-center justify-center rounded-2xl text-2xl font-black text-white"
                  style={{
                    width: 72,
                    height: 72,
                    background: `linear-gradient(135deg, ${primary}, ${secondary})`,
                    boxShadow: `0 0 32px ${primary}55`,
                  }}
                >
                  {data.venue.name.slice(0, 2).toUpperCase()}
                </div>
              )}
            </div>

            {/* Bottom content — venue name, tagline, CTA */}
            <div className="absolute left-0 right-0 px-6 max-w-2xl" style={{ bottom: "18%" }}>
              <h1
                data-testid="pm-venue-name"
                style={{
                  fontFamily: "var(--font-editorial)",
                  fontSize: "clamp(2.5rem, 9vw, 4rem)",
                  fontWeight: 600,
                  color: "#ffffff",
                  lineHeight: 1.1,
                  letterSpacing: "-0.01em",
                }}
              >
                {data.venue.name}
              </h1>

              <p
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: "0.9rem",
                  letterSpacing: "0.03em",
                  color: "rgba(255,255,255,0.6)",
                  fontWeight: 400,
                  marginTop: 8,
                }}
              >
                {data.venue.description || "Your loyalty journey starts here"}
              </p>

              {/* Whole-venue vegetarian statement — a line of text (not a pill/chip),
                  styled like the tagline. Over a cover photo white reads on the dark
                  overlay; with no cover it must stay readable on a light venue bg, so
                  it uses the same adaptive text the rest of the menu uses. The veg
                  symbol stays regulatory green. */}
              {pureVeg && (
                <p
                  data-testid="pm-pure-veg"
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    color: data.venue.cover_url ? "rgba(255,255,255,0.85)" : "var(--pm-adaptive-text)",
                    opacity: data.venue.cover_url ? 1 : 0.85,
                    marginTop: 16,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <span className="inline-flex items-center justify-center rounded-sm" style={{ width: 13, height: 13, border: "1.5px solid #22c55e", flexShrink: 0 }}>
                    <span className="rounded-full" style={{ width: 6, height: 6, background: "#22c55e" }} />
                  </span>
                  Pure vegetarian kitchen
                </p>
              )}

              {/* Explore CTA + bounce arrow */}
              <button
                onClick={scrollToMenu}
                className="mt-8 flex items-center gap-2 group"
                aria-label="Scroll to menu"
              >
                <span
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: "0.75rem",
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.60)",
                  }}
                >
                  Explore Menu
                </span>
                <ChevronDown
                  size={16}
                  className="pm-bounce"
                  style={{ color: "rgba(255,255,255,0.60)" }}
                />
              </button>
            </div>
          </>
        )}
      </section>

      {/* ============================================================
          SECTION 2 — STICKY SEARCH HEADER
          Fixed, slides in when hero scrolls out of view
      ============================================================ */}
      <div
        className="fixed inset-x-0 top-0 z-30 border-b"
        style={{
          background: `${branding.menu_background_color}f2`,
          borderColor: "rgba(255,255,255,0.07)",
          backdropFilter: "blur(14px)",
          transition: "transform 300ms ease, opacity 300ms ease",
          // Pinned whenever a search is active, so the input/clear/back are always
          // reachable even at the top of the page (otherwise the customer is trapped).
          transform: (heroVisible && !searchActive) ? "translateY(-100%)" : "translateY(0)",
          opacity: (heroVisible && !searchActive) ? 0 : 1,
          pointerEvents: (heroVisible && !searchActive) ? "none" : "auto",
        }}
      >
        <div className="max-w-2xl mx-auto px-4 py-2.5">
          <div className="flex items-center gap-2.5">
            {/* Venue name — collapses when search is focused */}
            <span
              className="font-semibold shrink-0 overflow-hidden whitespace-nowrap transition-all duration-200"
              style={{
                fontFamily: "var(--font-editorial)",
                fontSize: "0.95rem",
                color: branding.menu_text_color,
                maxWidth: searchFocused ? "0px" : "45%",
                opacity: searchFocused ? 0 : 1,
              }}
            >
              {data.venue.name}
            </span>

            {/* Search input */}
            <div className="relative flex-1">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                style={{ color: branding.menu_accent_text_color }}
              />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                onKeyDown={e => { if (e.key === "Escape") { setSearch(""); e.currentTarget.blur(); } }}
                placeholder="Search menu…"
                data-testid="pm-search"
                className="w-full rounded-full pl-8 pr-11 py-2 text-base border transition-all duration-200"
                style={{
                  background: branding.menu_card_color,
                  borderColor: search ? primary : "rgba(255,255,255,0.1)",
                  color: branding.menu_text_color,
                  outline: "none",
                }}
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                  data-testid="pm-search-clear"
                  className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center justify-center"
                  style={{ width: 44, height: 44, color: branding.menu_accent_text_color }}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ============================================================
          MENU SECTION (scroll target + all below-hero content)
      ============================================================ */}
      <div ref={menuSectionRef} style={{ scrollMarginTop: 0 }} />

      {/* ============================================================
          SECTION 2.5 — ORDER TYPE (unscoped "both" only)
          The choice lives on the menu, before anything is added (AD-2), so a seated guest isn't
          shown a takeaway-filtered menu they never asked for. A scoped link pins the type and omits
          this; single-mode venues and room visits have no choice to make. The checkout sheet keeps
          its own last-chance toggle. Guest wording: "Dine-in" / "Takeaway".
      ============================================================ */}
      {orderingMode === "both" && !isRoomOrder && !scopedType && (
        <div className="max-w-2xl mx-auto px-4 pt-4">
          <div
            className="rounded-xl border p-3"
            style={{
              borderColor: choosingType ? "rgba(245,158,11,0.45)" : "rgba(255,255,255,0.1)",
              background: "rgba(255,255,255,0.03)",
            }}
          >
            <p className="text-xs uppercase tracking-wider mb-2" style={{ color: branding.menu_accent_text_color }}>
              {choosingType ? "How are you ordering?" : "You're ordering for"}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {["dine_in", "takeaway"].map(t => {
                const selected = typeChosen && orderType === t;
                return (
                  <button
                    key={t}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => { setOrderType(t); setTypeChosen(true); }}
                    className="py-2.5 rounded-xl text-sm font-semibold border transition"
                    style={{
                      background: selected ? `${branding.menu_primary_color}20` : "transparent",
                      borderColor: selected ? branding.menu_primary_color : "rgba(255,255,255,0.12)",
                      color: selected ? branding.menu_primary_color : branding.menu_accent_text_color,
                    }}
                  >
                    {t === "dine_in" ? "Dine-in" : "Takeaway"}
                  </button>
                );
              })}
            </div>
            {choosingType && (
              <p className="text-[11px] mt-2" style={{ color: branding.menu_accent_text_color }}>
                Some dishes are available only one way — choose so we show you the right menu.
              </p>
            )}
            {/* A switch that strands cart lines is flagged here and blocked at checkout (AD-2). */}
            {!choosingType && unavailableCartLines.length > 0 && (
              <p className="text-[11px] mt-2" style={{ color: "#D97706" }}>
                {unavailableCartLines.length === 1 ? "1 item in your cart isn't" : `${unavailableCartLines.length} items in your cart aren't`} available for {orderTypeLabel(orderType)} — review your cart before checkout.
              </p>
            )}
          </div>
        </div>
      )}

      {/* ============================================================
          SECTION 3 — LOYALTY BAR
      ============================================================ */}
      {loyaltyEnabled && (
        <div className="max-w-2xl mx-auto pt-4">
          {/* Rate tagline above the form */}
          <p
            className="mx-4 mb-3 text-xs font-semibold"
            style={{ color: "var(--pm-adaptive-text)", fontFamily: "var(--font-body)" }}
          >
            🏆 Earn reward points on every order
          </p>
          <LoyaltyBar
            branding={branding}
            slug={slug}
            phase={phase}
            setPhase={setPhase}
            phone={phone}
            setPhone={setPhone}
            phoneErr={phoneErr}
            setPhoneErr={setPhoneErr}
            checking={checking}
            setChecking={setChecking}
            ptsResult={ptsResult}
            setPtsResult={setPtsResult}
            setPendingPhone={setPendingPhone}
            setRegResult={setRegResult}
            milestoneProgress={milestoneProgress}
            bottleLockerEnabled={bottleLockerEnabled}
          />

          {bottleLockerEnabled && (bottlePhase === "found" || bottlePhase === "registered") && (
            <div className="mx-4 mb-4">
              {/* Divider */}
              <div className="mb-3" style={{ height: "0.5px", background: "rgba(255,255,255,0.08)" }} />

              <p
                className="text-xs font-semibold uppercase tracking-wider mb-2"
                style={{ color: branding.menu_accent_text_color, fontFamily: "var(--font-body)" }}
              >
                🍾 Your stored bottles
              </p>

              {bottleResult?.active_tabs?.length === 0 ? (
                <div
                  className="flex items-start gap-3 rounded-xl p-4"
                  style={{ background: "rgba(255,255,255,0.06)", border: "0.5px solid rgba(255,255,255,0.1)" }}
                >
                  <div
                    className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
                    style={{ background: "rgba(127,119,221,0.2)" }}
                  >
                    <span style={{ fontSize: 16, color: branding.menu_primary_color }}>🍾</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold mb-1" style={{ color: branding.menu_text_color }}>
                      No bottles stored
                    </p>
                    <p className="text-xs leading-relaxed" style={{ color: branding.menu_text_color, opacity: 0.65 }}>
                      Buy a bottle. Store what's left. Saves you more than ordering peg by peg. Ask your server about our digital bottle locker.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {bottleResult.active_tabs.map((t) => {
                    const pct = t.bottle_size_ml > 0
                      ? Math.min(100, Math.round((t.remaining_ml / t.bottle_size_ml) * 100))
                      : 0;
                    const isExpiring = t.status === "Expiring Soon";
                    const barColor = isExpiring ? "#EF9F27" : branding.menu_primary_color;
                    return (
                      <div
                        key={t.id}
                        className="rounded-xl p-3"
                        style={{ background: "rgba(255,255,255,0.06)", border: "0.5px solid rgba(255,255,255,0.1)" }}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-semibold" style={{ color: branding.menu_text_color }}>
                            {t.brand}
                          </span>
                          <span
                            className="text-xs font-semibold px-2 py-0.5 rounded-full"
                            style={{
                              background: isExpiring ? "rgba(239,159,39,0.15)" : "rgba(29,158,117,0.15)",
                              color: isExpiring ? "#EF9F27" : "#1D9E75",
                              border: `0.5px solid ${isExpiring ? "rgba(239,159,39,0.4)" : "rgba(29,158,117,0.4)"}`,
                            }}
                          >
                            {t.status}
                          </span>
                        </div>
                        <p className="text-xs mb-2" style={{ color: branding.menu_text_color, opacity: 0.55 }}>
                          Expires {t.expiry_label}
                        </p>
                        <div
                          className="rounded-full overflow-hidden mb-1"
                          style={{ height: 3, background: "rgba(255,255,255,0.12)" }}
                        >
                          <div
                            className="rounded-full"
                            style={{ height: 3, width: `${pct}%`, background: barColor }}
                          />
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs" style={{ color: branding.menu_text_color, opacity: 0.55 }}>
                            {t.remaining_ml}ml remaining
                          </span>
                          <span className="text-xs" style={{ color: branding.menu_text_color, opacity: 0.4 }}>
                            {t.bottle_size_ml}ml
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Filters are exposed inside the category drawer only */}

      {/* ============================================================
          MAIN CONTENT
      ============================================================ */}
      {/* pb-32 clears the fixed floating Menu button (bottom-6 offset + its height)
          so the last grid row / item is never covered by it (#6). */}
      <main className="max-w-2xl mx-auto px-4 pb-32">
        {searchResults !== null ? (
          /* ─── Search results ─── */
          <div>
            <div className="flex items-center justify-between mb-3">
              <p
                className="text-xs font-semibold uppercase tracking-wider"
                style={{ color: branding.menu_accent_text_color }}
              >
                {searchResults.length > 0
                  ? `${searchResults.length} result${searchResults.length !== 1 ? "s" : ""} for "${search}"`
                  : `No results for "${search}"`}
              </p>
              <div className="flex items-center gap-3 shrink-0 ml-2">
                {globalFilters.length > 0 && (
                  <button
                    onClick={() => setGlobalFilters([])}
                    className="text-xs underline underline-offset-4"
                    style={{ color: branding.menu_primary_color }}
                  >
                    Clear filter
                  </button>
                )}
                <button
                  onClick={() => setSearch("")}
                  data-testid="pm-search-back"
                  className="text-xs font-semibold underline underline-offset-4"
                  style={{ color: primary }}
                >
                  Back to menu
                </button>
              </div>
            </div>
            {searchResults.length === 0 ? (
              <div className="text-center py-16" style={{ color: branding.menu_accent_text_color }}>
                <div className="text-4xl mb-3">🔍</div>
                <p className="text-sm">No items match your search.</p>
                <button
                  onClick={() => setSearch("")}
                  className="mt-3 text-xs underline underline-offset-4"
                  style={{ color: primary }}
                >
                  Clear search
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2.5">
                {searchResults.map(it => {
                  return (
                    <div key={it.id}>
                      <div
                        className="text-[10px] uppercase tracking-wider mb-1.5 px-1 font-semibold"
                        style={{ color: branding.menu_accent_text_color }}
                      >
                        {it.categoryName}
                      </div>
                      <ItemCard item={it} branding={branding} bottleMenuPromo={bottleMenuPromo} loyaltyEnabled={loyaltyEnabled} orderingQrEnabled={orderingQrEnabled} onOpen={openItem} onQuickAdd={quickAdd} cartQty={cart.find(cc => cc.key === cartLineKey(it.id, null, []))?.qty || 0} onUpdateQty={updateQty} catalog={data.tag_catalog || {}} addonCatalog={data.addon_catalog || {}} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          /* ─── SECTION 5: Two-level category navigation ─── */
          <>
            {hasMainCategories && !activeMCId ? (
              /* ── Main category grid ── */
              <>
                {/* Section divider header */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="h-px flex-1" style={{ background: "rgba(255,255,255,0.08)" }} />
                  <span className="font-bold" style={{ fontSize: "0.7rem", letterSpacing: "0.12em", textTransform: "uppercase", color: branding.menu_accent_text_color }}>
                    Menu
                  </span>
                  <div className="h-px flex-1" style={{ background: "rgba(255,255,255,0.08)" }} />
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  {mainCategories.map((mc, idx) => (
                    <MainCategoryCard
                      key={mc.id}
                      mainCategory={mc}
                      subCatCount={subCatCount[mc.id] || 0}
                      branding={branding}
                      onSelect={selectMainCategory}
                      index={idx}
                      nameFontSize={mainCategoryNameSize}
                    />
                  ))}
                </div>

                {/* Unassigned (top-level) categories — below the curated sections so
                    they stay first. Same "Other" heading the drawer uses. Rendered as
                    flat-mode cards; otherwise they match no main category and are
                    unreachable on this page. */}
                {unassignedCategories.length > 0 && (
                  <>
                    <div className="flex items-center gap-4 mb-4 mt-8">
                      <div className="h-px flex-1" style={{ background: "rgba(255,255,255,0.08)" }} />
                      <span className="font-bold" style={{ fontSize: "0.7rem", letterSpacing: "0.12em", textTransform: "uppercase", color: branding.menu_accent_text_color }}>
                        Other
                      </span>
                      <div className="h-px flex-1" style={{ background: "rgba(255,255,255,0.08)" }} />
                    </div>
                    <div className="grid grid-cols-2 gap-2.5">
                      {unassignedCategories.map((c, idx) => (
                        <CategoryCard
                          key={c.id}
                          category={c}
                          items={grouped[c.id] || []}
                          branding={branding}
                          onSelect={openCategory}
                          index={idx}
                          nameFontSize={unassignedNameSize}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              /* ── Sub-category grid (or flat grid when no main categories) ── */
              <>
                {/* Breadcrumb back button when inside a main category */}
                {hasMainCategories && activeMC && (
                  <button
                    onClick={goBackToMainCategories}
                    data-testid="pm-back-to-main-cats"
                    className="flex items-center gap-1.5 mb-4 group"
                    style={{ color: branding.menu_accent_text_color, fontFamily: "var(--font-body)" }}
                  >
                    <ChevronLeft size={16} style={{ color: primary }} />
                    <span className="text-sm font-semibold" style={{ color: "var(--pm-adaptive-text)" }}>
                      {activeMC.name}
                    </span>
                  </button>
                )}

                {/* Section divider */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="h-px flex-1" style={{ background: "rgba(255,255,255,0.08)" }} />
                  <span className="font-bold" style={{ fontSize: "0.7rem", letterSpacing: "0.12em", textTransform: "uppercase", color: branding.menu_accent_text_color }}>
                    {activeMC ? activeMC.name : "Menu"}
                  </span>
                  <div className="h-px flex-1" style={{ background: "rgba(255,255,255,0.08)" }} />
                </div>

                {/* 2-column sub-category grid */}
                {visibleCategories.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2.5">
                    {visibleCategories.map((c, idx) => (
                      <CategoryCard
                        key={c.id}
                        category={c}
                        items={grouped[c.id] || []}
                        branding={branding}
                        onSelect={openCategory}
                        index={idx}
                        nameFontSize={subCategoryNameSize}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16" style={{ color: branding.menu_accent_text_color }}>
                    <div className="text-3xl mb-3">🍽️</div>
                    <p className="text-sm">No categories here yet.</p>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>

      {/* === CATEGORY DRAWER === */}
      {openCategoryData && (
        <CategoryDrawer
          category={openCategoryData}
          items={grouped[openCategoryData.id] || []}
          branding={branding}
          // Only one overlay on screen at a time: while an item is open, ?c stays in the
          // URL (so back reopens the drawer here) but the drawer itself is held closed.
          isOpen={!!openCategoryId && !openItemId}
          onClose={closeCategory}
          loyaltyEnabled={loyaltyEnabled}
          bottleLockerEnabled={bottleLockerEnabled}
          bottleMenuPromo={bottleMenuPromo}
          globalFilters={globalFilters}
          onGlobalFilterChange={setGlobalFilters}
          availableFilters={availableFilters}
          disclaimers={data.venue.disclaimers}
          termsUrl={data.venue.terms_url || null}
          orderingQrEnabled={orderingQrEnabled}
          onOpenItem={openItem}
          onQuickAdd={quickAdd}
          cartQtyMap={Object.fromEntries(cart.map(c => [c.key, c.qty]))}
          onUpdateQty={updateQty}
          cartCount={cartCount}
          cartTotal={cartTotal}
          onViewCart={() => { closeCategory(); setCartOpen(true); }}
          catalog={data.tag_catalog || {}}
          addonCatalog={data.addon_catalog || {}}
        />
      )}

      {/* === ITEM VIEW / CUSTOMISATION SHEET === Rendered once here (not inside a card) so
          it survives the drawer closing behind it. Driven by ?item; interactive (variant +
          add-on selection with a running total) when ordering is on, else display-only. */}
      {openItemData && (
        <ItemView
          item={openItemData}
          branding={branding}
          loyaltyEnabled={loyaltyEnabled}
          catalog={data.tag_catalog || {}}
          addonCatalog={data.addon_catalog || {}}
          canOrder={orderingQrEnabled}
          editLine={editingLine && editingLine.item_id === openItemId ? editingLine : null}
          onSubmit={(sel) => { applySelection(sel); closeItemView(); }}
          onClose={closeItemView}
        />
      )}

      {/* === FLOATING MENU BUTTON === */}
      {!search && (
        <FloatingMenuSheet
          categories={data.categories}
          grouped={grouped}
          branding={branding}
          onSelect={openCategory}
          mainCategories={mainCategories}
        />
      )}

      {/* === STALE CART NUDGE ===
          A cart restored from 1–4h ago (see the restore effect) is old enough that it may be
          a previous visit's, so instead of silently carrying it we show a slim, dismissible
          line letting the guest review or clear it. Hidden once the cart is empty or a drawer
          is open. cartCount is the total quantity across lines. */}
      {orderingQrEnabled && staleCartPrompt && cartCount > 0 && !cartOpen && !checkoutOpen && openCategoryId === null && !openItemId && (
        <div
          data-testid="pm-stale-cart"
          className="fixed inset-x-0 top-0 z-40 flex items-center gap-3 px-4 pb-2.5 shadow-lg"
          style={{
            background: branding.menu_card_color,
            color: branding.menu_text_color,
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            paddingTop: "calc(10px + env(safe-area-inset-top, 0px))",
          }}
        >
          <span className="text-sm flex-1 min-w-0">
            You have <b>{cartCount}</b> {cartCount === 1 ? "item" : "items"} from earlier
          </span>
          <button
            type="button"
            onClick={() => { setCartOpen(true); setStaleCartPrompt(false); }}
            className="text-sm font-bold shrink-0"
            style={{ color: branding.menu_primary_color }}
          >
            Review
          </button>
          <button
            type="button"
            onClick={() => { clearCart(); setStaleCartPrompt(false); }}
            className="text-sm font-semibold shrink-0"
            style={{ color: branding.menu_accent_text_color }}
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => setStaleCartPrompt(false)}
            className="shrink-0 opacity-60"
            aria-label="Dismiss"
            style={{ color: branding.menu_text_color }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* === FLOATING CART BUTTON === */}
      {orderingQrEnabled && cartCount > 0 && !cartOpen && !checkoutOpen && openCategoryId === null && (
        <button
          type="button"
          onClick={() => setCartOpen(true)}
          className="fixed bottom-6 right-4 z-40 flex items-center gap-2 rounded-full px-4 py-3 shadow-2xl transition-all"
          style={{ background: branding.menu_primary_color, color: onPrimary(branding.menu_primary_color) }}
        >
          <ShoppingCart size={18} />
          <span className="font-bold text-sm">{cartCount}</span>
          <span className="font-semibold text-sm">₹{cartTotal.toFixed(0)}</span>
        </button>
      )}

      {/* === CART DRAWER === */}
      {cartOpen && (
        <>
          <div
            className="fixed inset-0 z-50"
            style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(5px)" }}
            onClick={() => setCartOpen(false)}
          />
          <div
            className="fixed inset-x-0 bottom-0 z-50 rounded-t-3xl flex flex-col"
            style={{
              background: branding.menu_background_color,
              maxHeight: "85dvh",
            }}
          >
            {/* Handle */}
            <div className="mx-auto mt-3 mb-1 h-1 w-10 rounded-full shrink-0" style={{ background: "rgba(255,255,255,0.18)" }} />

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b shrink-0" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
              <span className="font-semibold text-lg" style={{ color: branding.menu_text_color }}>
                Your Cart
              </span>
              <button type="button" onClick={() => setCartOpen(false)} style={{ color: branding.menu_accent_text_color }}>
                <X size={18} />
              </button>
            </div>

            {/* Cart items */}
            <div className="flex-1 overflow-y-auto px-4 py-3">
              {cart.length === 0 ? (
                <p className="text-center py-10 text-sm" style={{ color: branding.menu_accent_text_color }}>
                  Your cart is empty
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {/* The switch banner on the menu sends the guest here, so the cart must show the
                      problem — not leave it for Checkout one screen later (AE-3). Same wording as the
                      checkout panel; the two ways out are the per-line Remove (trash) and this switch. */}
                  {unavailableCartLines.length > 0 && (
                    <div className="rounded-xl p-3 mb-1 border" style={{ borderColor: "rgba(245,158,11,0.35)", background: "rgba(245,158,11,0.08)" }}>
                      <p className="text-sm font-semibold mb-1" style={{ color: branding.menu_text_color }}>
                        {unavailableCartLines.length === cart.length
                          ? `Nothing in your cart is available for ${orderTypeLabel(orderType)}`
                          : unavailableCartLines.length === 1
                            ? `One item isn't available for ${orderTypeLabel(orderType)}`
                            : `${unavailableCartLines.length} items aren't available for ${orderTypeLabel(orderType)}`}
                      </p>
                      <p className="text-xs" style={{ color: branding.menu_accent_text_color }}>
                        {orderingMode === "both" && !isRoomOrder && !scopedType
                          ? `Remove ${unavailableCartLines.length === 1 ? "it" : "them"} to continue, or switch back to ${orderTypeLabel(orderType === "takeaway" ? "dine_in" : "takeaway")}.`
                          : `Remove ${unavailableCartLines.length === 1 ? "it" : "them"} to continue.`}
                      </p>
                      {orderingMode === "both" && !isRoomOrder && !scopedType && (
                        <button
                          type="button"
                          onClick={() => setOrderType(orderType === "takeaway" ? "dine_in" : "takeaway")}
                          className="w-full mt-2 py-2 rounded-lg text-sm font-semibold border"
                          style={{ borderColor: branding.menu_primary_color, color: branding.menu_primary_color }}
                        >
                          Switch back to {orderTypeLabel(orderType === "takeaway" ? "dine_in" : "takeaway")}
                        </button>
                      )}
                    </div>
                  )}
                  {cart.map(c => {
                    // Variant + add-on names, so a line reads as exactly what was chosen.
                    const details = [c.variant_label, ...((c.addons || []).map(a => a.name))].filter(Boolean).join(" · ");
                    const customisable = Boolean(c.variant_id) || (c.addons || []).length > 0;
                    const lineUnavailable = unavailableKeys.has(c.key);
                    return (
                    <div key={c.key} className="flex items-center justify-between gap-3 py-2 border-b" style={{ borderColor: "rgba(255,255,255,0.06)", opacity: lineUnavailable ? 0.65 : 1 }}>
                      {/* Tapping the line re-opens the customisation sheet on it (edit in place). */}
                      <button type="button" onClick={() => editCartLine(c)} className="flex-1 min-w-0 text-left" style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}>
                        <p className="text-sm font-medium truncate" style={{ color: branding.menu_text_color }}>{c.name}</p>
                        {lineUnavailable && <p className="text-[11px] font-semibold" style={{ color: "#D97706" }}>Not available for {orderTypeLabel(orderType)}</p>}
                        {details && <p className="text-[11px] truncate" style={{ color: branding.menu_accent_text_color }}>{details}</p>}
                        <p className="text-xs" style={{ color: branding.menu_accent_text_color }}>
                          ₹{c.unit_price} each{customisable ? " · Edit" : ""}
                        </p>
                      </button>
                      <div className="flex items-center gap-2 shrink-0">
                        <button type="button" onClick={() => updateQty(c.key, -1)} className="flex h-7 w-7 items-center justify-center rounded-full border" style={{ borderColor: "rgba(255,255,255,0.15)", color: branding.menu_text_color }}>
                          <Minus size={12} />
                        </button>
                        <span className="text-sm font-bold w-5 text-center" style={{ color: branding.menu_text_color }}>{c.qty}</span>
                        <button type="button" onClick={() => updateQty(c.key, 1)} className="flex h-7 w-7 items-center justify-center rounded-full border" style={{ borderColor: "rgba(255,255,255,0.15)", color: branding.menu_text_color }}>
                          <Plus size={12} />
                        </button>
                        <button type="button" onClick={() => removeFromCart(c.key)} className="ml-1" style={{ color: "#EF4444" }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <span className="text-sm font-bold shrink-0 w-14 text-right" style={{ color: branding.menu_primary_color }}>
                        ₹{(c.unit_price * c.qty).toFixed(0)}
                      </span>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {cart.length > 0 && (
              <div className="px-4 py-4 border-t shrink-0" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
                {lastOpenCategoryId && (
                  <button
                    type="button"
                    onClick={() => { setCartOpen(false); openCategory(lastOpenCategoryId); }}
                    className="w-full mb-3 py-2 text-xs font-semibold underline underline-offset-4"
                    style={{ color: branding.menu_accent_text_color }}
                  >
                    + Add more items
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setCartOpen(false); setCheckoutOpen(true); }}
                  disabled={unavailableCartLines.length > 0}
                  className="w-full py-3.5 rounded-xl font-bold text-sm flex items-center justify-between px-4 disabled:opacity-50"
                  style={{ background: branding.menu_primary_color, color: onPrimary(branding.menu_primary_color) }}
                >
                  {unavailableCartLines.length > 0 ? (
                    <span className="w-full text-center">Remove unavailable items</span>
                  ) : (
                    <>
                      <span>Proceed to Checkout</span>
                      <span>₹{cartTotal.toFixed(0)}</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* === CHECKOUT MODAL === */}
      {checkoutOpen && !orderResult && (
        <>
          <div
            className="fixed inset-0 z-50"
            style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(5px)" }}
            onClick={() => setCheckoutOpen(false)}
          />
          <div
            className="fixed inset-x-0 bottom-0 z-50 rounded-t-3xl flex flex-col"
            style={{ background: branding.menu_background_color, maxHeight: "90dvh" }}
          >
            <div className="mx-auto mt-3 mb-1 h-1 w-10 rounded-full shrink-0" style={{ background: "rgba(255,255,255,0.18)" }} />

            <div className="flex items-center justify-between px-5 py-3 border-b shrink-0" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
              <button type="button" onClick={() => { setCheckoutOpen(false); setCartOpen(true); }} style={{ color: branding.menu_accent_text_color }}>
                <ChevronLeft size={20} />
              </button>
              <span className="font-semibold text-lg" style={{ color: branding.menu_text_color }}>Checkout</span>
              <button type="button" onClick={() => setCheckoutOpen(false)} style={{ color: branding.menu_accent_text_color }}>
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-4">

              {/* Order type — only when the mode supports both, not in room context, and not on a
                  scoped link (which pins the type the link was shared for). */}
              {orderingMode === "both" && !isRoomOrder && !scopedType && (
                <div>
                  <p className="text-xs uppercase tracking-wider mb-2" style={{ color: branding.menu_accent_text_color }}>
                    {typeChosen ? "Order type" : "Choose your order type"}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {["dine_in", "takeaway"].map(t => {
                      // Highlight only once the guest has actually chosen (AD-1) — no pre-selected guess.
                      const selected = typeChosen && orderType === t;
                      return (
                      <button
                        key={t}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => { setOrderType(t); setTypeChosen(true); }}
                        className="py-2.5 rounded-xl text-sm font-semibold border transition"
                        style={{
                          background: selected ? `${branding.menu_primary_color}20` : "transparent",
                          borderColor: selected ? branding.menu_primary_color : "rgba(255,255,255,0.1)",
                          color: selected ? branding.menu_primary_color : branding.menu_accent_text_color,
                        }}
                      >
                        {t === "dine_in" ? "Dine-in" : "Takeaway"}
                      </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Items this order type can't carry (AC-2) — named, with the reason, each removable
                  right here, and a one-tap switch back to the type that can serve them. Checkout
                  stays blocked (see the pinned button) until none remain. Shows after a toggle
                  switch and for a cart restored from a visit on a different order type alike. */}
              {unavailableCartLines.length > 0 && (
                <div className="rounded-xl p-4 border" style={{ borderColor: "rgba(245,158,11,0.35)", background: "rgba(245,158,11,0.08)" }}>
                  <p className="text-sm font-semibold mb-1" style={{ color: branding.menu_text_color }}>
                    {unavailableCartLines.length === cart.length
                      ? `Nothing in your cart is available for ${orderTypeLabel(orderType)}`
                      : unavailableCartLines.length === 1
                        ? `One item isn't available for ${orderTypeLabel(orderType)}`
                        : `${unavailableCartLines.length} items aren't available for ${orderTypeLabel(orderType)}`}
                  </p>
                  <p className="text-xs mb-3" style={{ color: branding.menu_accent_text_color }}>
                    {orderingMode === "both" && !isRoomOrder && !scopedType
                      ? `Remove ${unavailableCartLines.length === 1 ? "it" : "them"} to continue, or switch back to ${orderTypeLabel(orderType === "takeaway" ? "dine_in" : "takeaway")}.`
                      : `Remove ${unavailableCartLines.length === 1 ? "it" : "them"} to continue.`}
                  </p>
                  <div className="flex flex-col gap-2">
                    {unavailableCartLines.map(c => (
                      <div key={c.key} className="flex items-center justify-between gap-3">
                        <span className="min-w-0 truncate text-sm" style={{ color: branding.menu_text_color }}>
                          {c.qty}× {c.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFromCart(c.key)}
                          className="shrink-0 text-xs font-semibold rounded-lg px-3 py-1.5 border"
                          style={{ borderColor: "rgba(239,68,68,0.4)", color: "#EF4444" }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                  {orderingMode === "both" && !isRoomOrder && !scopedType && (
                    <button
                      type="button"
                      onClick={() => setOrderType(orderType === "takeaway" ? "dine_in" : "takeaway")}
                      className="w-full mt-3 py-2.5 rounded-xl text-sm font-semibold border"
                      style={{ borderColor: branding.menu_primary_color, color: branding.menu_primary_color }}
                    >
                      Switch back to {orderTypeLabel(orderType === "takeaway" ? "dine_in" : "takeaway")}
                    </button>
                  )}
                </div>
              )}

              {/* Table number — only for dine-in, hidden in room context */}
              {!isRoomOrder && (orderingMode === "dine_in" || (orderingMode === "both" && orderType === "dine_in")) && (
                <div>
                  <p className="text-xs uppercase tracking-wider mb-2" style={{ color: branding.menu_accent_text_color }}>Table number</p>
                  <select
                    value={orderTable}
                    onChange={e => setOrderTable(e.target.value)}
                    className="w-full rounded-xl px-4 py-3 text-sm border"
                    style={{
                      background: "rgba(255,255,255,0.05)",
                      borderColor: "rgba(255,255,255,0.1)",
                      color: branding.menu_text_color,
                      fontSize: 16,
                      outline: "none",
                    }}
                  >
                    <option value="">Select your table</option>
                    {attachToCheck
                      ? attachSPs
                          .filter(s => s.level === "table" && (!spParam || s.id === spParam || s.parent_id === spParam))
                          .map(t => (
                            <option key={t.id} value={t.id}>
                              {t.label}{t.running ? ` · ₹${Math.round(t.running.total)} running` : ""}
                            </option>
                          ))
                      : Array.from({ length: orderingTableCount }, (_, i) => i + 1).map(n => (
                          <option key={n} value={n}>Table {n}</option>
                        ))}
                  </select>
                </div>
              )}

              {/* Room number — only in room-service context */}
              {isRoomOrder && (
                <div>
                  <p className="text-xs uppercase tracking-wider mb-2" style={{ color: branding.menu_accent_text_color }}>
                    Room number <span style={{ color: "#EF4444" }}>*</span>
                  </p>
                  <input
                    type="text"
                    value={orderRoom}
                    onChange={e => setOrderRoom(e.target.value.slice(0, 10))}
                    placeholder="e.g. 204, 1204A"
                    maxLength={10}
                    autoComplete="off"
                    className="w-full rounded-xl px-4 py-3 text-sm border"
                    style={{
                      background: "rgba(255,255,255,0.05)",
                      borderColor: "rgba(255,255,255,0.1)",
                      color: branding.menu_text_color,
                      fontSize: 16,
                      outline: "none",
                    }}
                  />
                </div>
              )}

              {/* Room service closed message */}
              {isRoomOrder && !roomServiceOpenNow && (
                <div className="rounded-xl p-4 text-center text-sm" style={{ background: "rgba(239,68,68,0.1)", color: "#EF4444", border: "1px solid rgba(239,68,68,0.3)" }}>
                  Room service is available {roomServiceStart}–{roomServiceEnd}. You can browse the menu, but ordering is closed right now. For urgent requests, please contact the front desk.
                </div>
              )}

              {/* ── Phone — always first, always required ── */}
              <div>
                <p className="text-xs uppercase tracking-wider mb-2" style={{ color: branding.menu_accent_text_color }}>
                  Mobile number <span style={{ color: "#EF4444" }}>*</span>
                </p>
                <div style={{ position: "relative" }}>
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={orderPhone}
                    onChange={e => {
                      const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setOrderPhone(digits);
                      if (digits.length < 10) {
                        // Reset lookup when phone changes below 10 digits
                        setCheckoutLookupDone(false);
                        setCheckoutFoundCustomer(null);
                        setCheckoutReturningOrderer(false);
                        // Only clear a name the lookup auto-filled — a name the guest typed
                        // themselves must survive editing the phone (they may be fixing a typo).
                        if (orderNameAutoRef.current) {
                          setOrderName("");
                          orderNameAutoRef.current = false;
                        }
                      } else {
                        lookupCheckoutPhone(digits);
                      }
                    }}
                    placeholder="10-digit mobile number"
                    maxLength={10}
                    autoComplete="tel"
                    className="w-full rounded-xl px-4 py-3 text-sm border"
                    style={{
                      background: "rgba(255,255,255,0.05)",
                      borderColor: "rgba(255,255,255,0.1)",
                      color: branding.menu_text_color,
                      fontSize: 16,
                      outline: "none",
                    }}
                  />
                  {checkoutLookupLoading && (
                    <div style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)" }}>
                      <div
                        className="animate-spin rounded-full border-2"
                        style={{ width: 16, height: 16, borderColor: "rgba(255,255,255,0.15)", borderTopColor: branding.menu_primary_color }}
                      />
                    </div>
                  )}
                </div>

                {/* Recognition feedback after lookup */}
                {checkoutLookupDone && checkoutFoundCustomer && (
                  <p className="text-xs mt-1.5 font-medium" style={{ color: branding.menu_primary_color }}>
                    👤 Welcome back · {checkoutFoundCustomer.points_balance} pts
                  </p>
                )}
                {checkoutLookupDone && checkoutReturningOrderer && !checkoutFoundCustomer && (
                  <p className="text-xs mt-1.5" style={{ color: branding.menu_accent_text_color, opacity: 0.7 }}>
                    Welcome back!
                  </p>
                )}
              </div>

              {/* ── Name — shown after 10-digit phone entered, always editable ── */}
              {orderPhone.replace(/\D/g, "").length === 10 && (
                <div>
                  <p className="text-xs uppercase tracking-wider mb-2" style={{ color: branding.menu_accent_text_color }}>
                    Your name <span style={{ color: "#EF4444" }}>*</span>
                  </p>
                  <input
                    type="text"
                    value={orderName}
                    // The guest now owns the name — a later phone edit must not wipe it.
                    onChange={e => { setOrderName(e.target.value); orderNameAutoRef.current = false; }}
                    placeholder="First name"
                    autoComplete="name"
                    className="w-full rounded-xl px-4 py-3 text-sm border"
                    style={{
                      background: "rgba(255,255,255,0.05)",
                      borderColor: "rgba(255,255,255,0.1)",
                      color: branding.menu_text_color,
                      fontSize: 16,
                      outline: "none",
                    }}
                  />
                  {checkoutFoundCustomer && (
                    <p className="text-xs mt-1" style={{ color: branding.menu_accent_text_color, opacity: 0.6 }}>
                      From your loyalty account — edit if needed
                    </p>
                  )}
                  {checkoutReturningOrderer && !checkoutFoundCustomer && (
                    <p className="text-xs mt-1" style={{ color: branding.menu_accent_text_color, opacity: 0.6 }}>
                      From your last order — edit if needed
                    </p>
                  )}
                </div>
              )}

              {/* Loyalty consent — pre-ticked, only shown when loyalty is enabled */}
              {loyaltyEnabled && orderingQrEnabled && !checkoutFoundCustomer && (
                <div>
                  <label
                    className="flex items-start gap-3 cursor-pointer select-none"
                    onClick={() => setEnrollLoyalty(e => !e)}
                  >
                    <div
                      className="shrink-0 mt-0.5 flex items-center justify-center rounded"
                      style={{
                        width: 20, height: 20,
                        border: `2px solid ${enrollLoyalty ? branding.menu_primary_color : "rgba(255,255,255,0.2)"}`,
                        background: enrollLoyalty ? branding.menu_primary_color : "transparent",
                        transition: "all 0.15s",
                      }}
                    >
                      {enrollLoyalty && (
                        <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                          <path d="M1 4L4 7L10 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium" style={{ color: branding.menu_text_color }}>
                        Earn points on this order
                      </p>
                      <p className="text-xs mt-0.5" style={{ color: branding.menu_accent_text_color, opacity: 0.75 }}>
                        Join {data?.venue?.name}'s loyalty programme and earn{" "}
                        {Math.floor(cartTotal * ((data?.venue?.points_rate_pct || 10) / 100))} pts
                        {" "}worth ₹{Math.floor(cartTotal * ((data?.venue?.points_rate_pct || 10) / 100))}.
                        Get order updates on WhatsApp. You can opt out anytime.
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {/* Vehicle number — takeaway only, when the venue flag is on. Free text, no plate regex. */}
              {!isRoomOrder && orderType === "takeaway" && vehicleEnabled && (
                <div>
                  <p className="text-xs uppercase tracking-wider mb-2" style={{ color: branding.menu_accent_text_color }}>
                    Vehicle number <span style={{ opacity: 0.5, fontSize: 10, textTransform: "none" }}>(optional)</span>
                  </p>
                  <input
                    type="text"
                    value={orderVehicle}
                    onChange={e => setOrderVehicle(e.target.value.toUpperCase().slice(0, 15))}
                    placeholder="e.g. MH12AB1234"
                    maxLength={15}
                    className="w-full rounded-xl px-4 py-3 text-sm border"
                    style={{
                      background: "rgba(255,255,255,0.05)",
                      borderColor: "rgba(255,255,255,0.1)",
                      color: branding.menu_text_color,
                      fontSize: 16,
                      outline: "none",
                      textTransform: "uppercase",
                    }}
                  />
                </div>
              )}

              {/* Special note */}
              <div>
                <p className="text-xs uppercase tracking-wider mb-2" style={{ color: branding.menu_accent_text_color }}>
                  Special instructions <span style={{ opacity: 0.5, fontSize: 10, textTransform: "none" }}>(optional)</span>
                </p>
                <textarea
                  value={orderNote}
                  onChange={e => setOrderNote(e.target.value)}
                  placeholder="Any special requests for this order…"
                  rows={2}
                  className="w-full rounded-xl px-4 py-3 text-sm border resize-none"
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    borderColor: "rgba(255,255,255,0.1)",
                    color: branding.menu_text_color,
                    fontSize: 16,
                    outline: "none",
                  }}
                />
              </div>

              {/* Order summary */}
              <div className="rounded-xl p-4 border" style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)" }}>
                <p className="text-xs uppercase tracking-wider mb-3" style={{ color: branding.menu_accent_text_color }}>Order summary</p>
                {availableCartLines.map(c => {
                  const details = [c.variant_label, ...((c.addons || []).map(a => a.name))].filter(Boolean).join(" · ");
                  return (
                  <div key={c.key} className="flex justify-between text-sm mb-1.5 gap-3">
                    <span className="min-w-0" style={{ color: branding.menu_text_color }}>
                      {c.qty}× {c.name}
                      {details && <span className="block text-[11px] truncate" style={{ color: branding.menu_accent_text_color }}>{details}</span>}
                    </span>
                    <span className="shrink-0" style={{ color: branding.menu_primary_color }}>₹{(c.unit_price * c.qty).toFixed(0)}</span>
                  </div>
                  );
                })}
                {/* Blocked lines are shown but NOT totalled (AE-4) — struck through and labelled, so the
                    guest can see they aren't part of the amount. The order stays blocked until they go. */}
                {unavailableCartLines.length > 0 && (
                  <div className="mt-2 pt-2 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
                    <p className="text-[11px] uppercase tracking-wider mb-1.5" style={{ color: "#D97706" }}>Not included — not available for {orderTypeLabel(orderType)}</p>
                    {unavailableCartLines.map(c => {
                      const details = [c.variant_label, ...((c.addons || []).map(a => a.name))].filter(Boolean).join(" · ");
                      return (
                      <div key={c.key} className="flex justify-between text-sm mb-1.5 gap-3" style={{ opacity: 0.6 }}>
                        <span className="min-w-0 line-through" style={{ color: branding.menu_text_color }}>
                          {c.qty}× {c.name}
                        </span>
                        <span className="shrink-0 line-through" style={{ color: branding.menu_accent_text_color }}>₹{(c.unit_price * c.qty).toFixed(0)}</span>
                      </div>
                      );
                    })}
                  </div>
                )}
                {/* Server-priced breakdown (Commit 1): subtotal, each tax line, charges, round-off,
                    total — all from the bill object. Hidden when nothing is payable (AE-4: every line
                    blocked), so no stray total shows. While re-pricing, show the last figures faintly. */}
                {availableCartLines.length > 0 && (
                <div className="mt-3 pt-3 border-t" style={{ borderColor: "rgba(255,255,255,0.08)", opacity: pricing ? 0.5 : 1 }}>
                  <div className="flex justify-between text-sm mb-1" style={{ color: branding.menu_accent_text_color }}>
                    <span>Subtotal</span><span>₹{Number(bill ? bill.subtotal : availableTotal).toFixed(2)}</span>
                  </div>
                  {bill && (bill.tax_lines || []).map((t, i) => (
                    <div key={`tax${i}`} className="flex justify-between text-sm mb-1" style={{ color: branding.menu_accent_text_color }}>
                      <span>{t.label}</span><span>₹{(Number(t.amount) || 0).toFixed(2)}</span>
                    </div>
                  ))}
                  {bill && (bill.charges || []).map((c, i) => (
                    <div key={`chg${i}`} className="flex justify-between text-sm mb-1" style={{ color: branding.menu_accent_text_color }}>
                      <span>{c.label || "Charge"}</span><span>₹{(Number(c.amount) || 0).toFixed(2)}</span>
                    </div>
                  ))}
                  {bill && Number(bill.round_off) !== 0 && (
                    <div className="flex justify-between text-sm mb-1" style={{ color: branding.menu_accent_text_color }}>
                      <span>Round off</span><span>{Number(bill.round_off) < 0 ? "−" : ""}₹{Math.abs(Number(bill.round_off) || 0).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-sm mt-2 pt-2 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
                    <span style={{ color: branding.menu_text_color }}>Total</span>
                    <span style={{ color: branding.menu_primary_color }}>{bill ? `₹${bill.rounded_total}` : "…"}</span>
                  </div>
                </div>
                )}
              </div>

              {!orderingOpen && (
                <div className="rounded-xl p-4 text-center text-sm" style={{ background: "rgba(239,68,68,0.1)", color: "#EF4444", border: "1px solid rgba(239,68,68,0.3)" }}>
                  This venue is not accepting orders right now. Please check back later.
                </div>
              )}

              {/* Payment method — shown only when takeaway offers more than one.
                  Online-only shows no choice (the button just says "Pay"). */}
              {onlineAvailable && takeawayHasChoice && (
                <div>
                  <div className="text-xs font-semibold mb-2" style={{ color: branding.menu_accent_text_color }}>
                    Payment
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { v: "online", label: "Pay online" },
                      { v: "cash", label: "Pay at counter" },
                    ].map((o) => (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => setPayMethod(o.v)}
                        className="py-2.5 rounded-xl text-sm font-semibold border transition"
                        style={{
                          background: payMethod === o.v ? `${branding.menu_primary_color}20` : "transparent",
                          borderColor: payMethod === o.v ? branding.menu_primary_color : "rgba(255,255,255,0.12)",
                          color: payMethod === o.v ? branding.menu_primary_color : branding.menu_accent_text_color,
                        }}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

            </div>

            {/* Pinned CTA — this button was the last child of the scrollable body, so on
                short viewports it sat below the fold and the guest could neither see the total
                nor place the order without scrolling. Pin it to the bottom of the sheet (as the
                item sheet's Add button already is), respecting the iOS safe-area inset. */}
            <div
              className="shrink-0"
              style={{
                padding: "12px 16px calc(12px + env(safe-area-inset-bottom, 0px))",
                borderTop: "1px solid rgba(255,255,255,0.08)",
                background: branding.menu_background_color,
              }}
            >
              <button
                type="button"
                onClick={placeOrder}
                disabled={orderPlacing || pricing || !bill || !orderingOpen || cart.length === 0 || choosingType || unavailableCartLines.length > 0 || (isRoomOrder && !roomServiceOpenNow)}
                className="w-full py-4 rounded-xl font-bold text-base disabled:opacity-50 transition"
                style={{ background: branding.menu_primary_color, color: onPrimary(branding.menu_primary_color) }}
              >
                {orderPlacing
                  ? "Placing order…"
                  : choosingType
                    ? "Choose an order type"
                    : unavailableCartLines.length > 0
                      ? "Remove unavailable items"
                      : (pricing || !bill)
                        ? "Calculating…"
                        : effectivePayMethod === "online"
                          ? `Pay ₹${bill.rounded_total}`
                          : `Place Order · ₹${bill.rounded_total}`}
              </button>
            </div>
          </div>
        </>
      )}

      {/* === ROOM DELIVERY CONFIRM DIALOG === */}
      {roomConfirmOpen && (
        <>
          <div
            className="fixed inset-0 z-[60]"
            style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)" }}
            onClick={() => setRoomConfirmOpen(false)}
          />
          <div
            className="fixed inset-x-0 bottom-0 z-[60] rounded-t-3xl flex flex-col items-center text-center"
            style={{ background: branding.menu_background_color, padding: "2rem 1.5rem 2.5rem" }}
          >
            <p className="text-lg font-bold mb-1" style={{ color: branding.menu_text_color }}>
              Deliver to Room {orderRoom.trim()}?
            </p>
            <p className="text-sm mb-5" style={{ color: branding.menu_accent_text_color }}>
              Please confirm your room number before placing the order.
            </p>
            <div className="flex gap-3 w-full max-w-xs">
              <button
                type="button"
                onClick={() => setRoomConfirmOpen(false)}
                className="flex-1 py-3 rounded-xl font-semibold text-sm border transition"
                style={{ borderColor: "rgba(255,255,255,0.15)", color: branding.menu_accent_text_color }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={_submitOrder}
                disabled={orderPlacing}
                className="flex-1 py-3 rounded-xl font-bold text-sm disabled:opacity-50 transition"
                style={{ background: branding.menu_primary_color, color: onPrimary(branding.menu_primary_color) }}
              >
                {orderPlacing ? "Placing…" : "Confirm"}
              </button>
            </div>
          </div>
        </>
      )}

      {/* === ADD-TO-RUNNING-BILL CONFIRMATION (QR dine-in, flag on) === */}
      {checkConfirmOpen && (() => {
        const sp = attachSPs.find(s => s.id === orderTable);
        const area = sp && attachSPs.find(a => a.id === sp.parent_id);
        return (
          <>
            <div className="fixed inset-0 z-[70]" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)" }} onClick={() => setCheckConfirmOpen(false)} />
            <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] w-[min(360px,90vw)] rounded-2xl p-5" style={{ background: branding.menu_card_color }}>
              <p className="text-base font-bold mb-1" style={{ color: branding.menu_text_color }}>
                {area ? `${area.label} · ` : ""}{sp ? sp.label : "This table"}
              </p>
              <p className="text-sm mb-4" style={{ color: branding.menu_accent_text_color }}>
                ₹{sp?.running ? Math.round(sp.running.total) : 0} already running ({sp?.running?.item_count || 0} items). Add your order to this table's bill?
              </p>
              <div className="flex gap-3">
                <button type="button" onClick={() => setCheckConfirmOpen(false)} className="flex-1 py-3 rounded-xl font-semibold text-sm border" style={{ borderColor: "rgba(255,255,255,0.15)", color: branding.menu_accent_text_color }}>No, choose another</button>
                <button type="button" onClick={_submitOrder} disabled={orderPlacing} className="flex-1 py-3 rounded-xl font-bold text-sm disabled:opacity-50" style={{ background: branding.menu_primary_color, color: onPrimary(branding.menu_primary_color) }}>{orderPlacing ? "Adding…" : "Yes, add to this table"}</button>
              </div>
            </div>
          </>
        );
      })()}

      {/* === ORDER CONFIRMATION === */}
      {checkoutOpen && orderResult && (
        <>
          <div
            className="fixed inset-0 z-50"
            style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }}
          />
          <div
            className="fixed inset-x-0 bottom-0 z-50 rounded-t-3xl flex flex-col items-center text-center"
            style={{ background: branding.menu_background_color, padding: "2.5rem 1.5rem 3rem" }}
          >
            <CheckCircle2 size={52} style={{ color: branding.menu_primary_color }} className="mb-4" />
            <h2 className="text-2xl font-black mb-2" style={{ color: branding.menu_text_color, fontFamily: "var(--font-editorial)" }}>
              {orderResult.attached ? "Added to your table! 🎉" : "Order Placed! 🎉"}
            </h2>
            <p className="text-sm mb-1" style={{ color: branding.menu_accent_text_color }}>
              {orderResult.attached ? `Running bill #${orderResult.check_number}` : `Order #${orderResult.order_number}`}
            </p>
            <p className="text-lg font-bold mb-4" style={{ color: branding.menu_primary_color }}>
              ₹{(orderResult.total || 0).toFixed(0)}
            </p>
            <p className="text-sm mb-6" style={{ color: branding.menu_accent_text_color }}>
              {isRoomOrder
                ? "Your order will be delivered to your room."
                : orderPhone ? "You'll receive a WhatsApp message when your order is ready." : "We'll let you know when your order is ready."}
            </p>
            <button
              type="button"
              onClick={() => {
                setCheckoutOpen(false);
                setOrderResult(null);
                setOrderName("");
                orderNameAutoRef.current = false;
                setOrderPhone("");
                setOrderTable("");
                setOrderRoom("");
                setOrderNote("");
                setOrderType(isRoomOrder ? "room" : orderingMode === "dine_in" ? "dine_in" : "takeaway");
                setEnrollLoyalty(true);
                setCheckoutLookupDone(false);
                setCheckoutFoundCustomer(null);
                setCheckoutReturningOrderer(false);
                setCheckoutLookupLoading(false);
              }}
              className="w-full py-3.5 rounded-xl font-bold text-sm"
              style={{ background: branding.menu_primary_color, color: onPrimary(branding.menu_primary_color) }}
            >
              Back to Menu
            </button>
          </div>
        </>
      )}

      {/* === FLOATING MY BOTTLES BUTTON === */}
      {!search && bottleLockerEnabled && !loyaltyEnabled && !bottleSheetOpen && (
        <button
          data-testid="pm-floating-bottle-btn"
          className="fixed bottom-20 right-4 z-40 flex items-center gap-2 rounded-full px-5 py-3 shadow-xl transition-all duration-200 hover:scale-105 active:scale-95 tb-fade-up"
          style={{ background: primary, color: onPrimary(primary), animationDelay: "0.5s" }}
          aria-label="Check your bottles"
          onClick={() => setBottleSheetOpen(true)}
        >
          <span style={{ fontSize: 15, lineHeight: 1 }}>🍾</span>
          <span className="text-sm font-semibold">My Bottles</span>
        </button>
      )}

      {/* === MY BOTTLES MODAL === */}
      {bottleLockerEnabled && !loyaltyEnabled && bottleSheetOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 z-50"
            style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(5px)" }}
            onClick={bottleSheetClose}
          />

          {/* Modal — top-anchored, stays visible when keyboard opens */}
          <div
            className="fixed z-50 flex flex-col rounded-2xl overflow-hidden"
            style={{
              top: "10dvh",
              left: "16px",
              right: "16px",
              maxHeight: "80dvh",
              background: branding.menu_background_color,
            }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-5 py-4 border-b shrink-0"
              style={{ borderColor: "rgba(255,255,255,0.08)" }}
            >
              <div>
                <span
                  className="font-medium text-lg block"
                  style={{ fontFamily: "var(--font-editorial)", color: branding.menu_text_color }}
                >
                  My Bottles
                </span>
                <span
                  className="text-xs block mt-0.5"
                  style={{ color: branding.menu_accent_text_color, opacity: 0.7 }}
                >
                  Check your stored bottles at this venue
                </span>
              </div>
              <button
                onClick={bottleSheetClose}
                className="flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-white/10"
                style={{ color: branding.menu_accent_text_color }}
                aria-label="Close"
              >
                <X size={15} />
              </button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-4 pt-4 pb-6">

              {/* Phone input — always visible */}
              <form onSubmit={checkBottle} className="flex gap-2 mb-3">
                <input
                  type="tel"
                  inputMode="numeric"
                  value={bottlePhone}
                  onChange={(e) => {
                    setBottlePhone(e.target.value);
                    setBottlePhoneErr("");
                    if (bottlePhase !== "idle" && bottlePhase !== "loading") {
                      setBottlePhase("idle");
                      setBottleResult(null);
                      setBottleNameInput("");
                      setBottleNameErr("");
                    }
                  }}
                  placeholder="10-digit mobile number"
                  maxLength={13}
                  disabled={bottlePhase === "loading"}
                  className="flex-1 min-w-0 border rounded-lg px-3 py-2"
                  style={{
                    background: "rgba(255,255,255,0.06)",
                    borderColor: bottlePhoneErr ? "#EF4444" : "rgba(255,255,255,0.15)",
                    color: branding.menu_text_color,
                    outline: "none",
                    fontSize: 16,
                    opacity: bottlePhase === "loading" ? 0.5 : 1,
                  }}
                />
                <button
                  type="submit"
                  disabled={bottlePhase === "loading" || !bottlePhone.trim()}
                  className="rounded-lg px-4 py-2 font-semibold text-sm shrink-0"
                  style={{
                    background: primary,
                    color: onPrimary(primary),
                    border: "none",
                    opacity: (bottlePhase === "loading" || !bottlePhone.trim()) ? 0.5 : 1,
                  }}
                >
                  {bottlePhase === "loading" ? "…" : "Check"}
                </button>
              </form>
              {bottlePhoneErr && <p className="text-xs text-red-400 mb-3">{bottlePhoneErr}</p>}
              {(bottlePhase === "found" || bottlePhase === "not_found" || bottlePhase === "registered") && (
                <div className="flex justify-end mb-1">
                  <button
                    onClick={() => {
                      setBottlePhase("idle");
                      setBottleResult(null);
                      setBottlePhone("");
                      setBottleNameInput("");
                      setBottleNameErr("");
                      setBottlePhoneErr("");
                    }}
                    className="text-xs underline underline-offset-2 shrink-0"
                    style={{ color: branding.menu_accent_text_color }}
                  >
                    clear
                  </button>
                </div>
              )}

              {/* Loading */}
              {bottlePhase === "loading" && (
                <p className="text-sm text-center py-8" style={{ color: branding.menu_text_color, opacity: 0.5 }}>Checking...</p>
              )}

              {/* Found or registered — show tabs */}
              {(bottlePhase === "found" || bottlePhase === "registered") && (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: branding.menu_text_color, opacity: 0.6 }}>
                    Your stored bottles
                  </p>
                  {bottleResult?.active_tabs?.length === 0 ? (
                    <div className="flex items-start gap-3 rounded-xl p-4" style={{ background: "rgba(255,255,255,0.06)", border: "0.5px solid rgba(255,255,255,0.1)" }}>
                      <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(127,119,221,0.2)" }}>
                        <span style={{ fontSize: 16, color: primary }}>🍾</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold mb-1" style={{ color: branding.menu_text_color }}>No bottles stored</p>
                        <p className="text-xs leading-relaxed" style={{ color: branding.menu_text_color, opacity: 0.65 }}>
                          Buy a bottle. Store what's left. Saves you more than ordering peg by peg. Ask your server about our digital bottle locker.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {bottleResult.active_tabs.map((t) => {
                        const pct = t.bottle_size_ml > 0 ? Math.min(100, Math.round((t.remaining_ml / t.bottle_size_ml) * 100)) : 0;
                        const isExpiring = t.status === "Expiring Soon";
                        const barColor = isExpiring ? "#EF9F27" : primary;
                        return (
                          <div key={t.id} className="rounded-xl p-3" style={{ background: "rgba(255,255,255,0.06)", border: "0.5px solid rgba(255,255,255,0.1)" }}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-semibold" style={{ color: branding.menu_text_color }}>{t.brand}</span>
                              <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{
                                background: isExpiring ? "rgba(239,159,39,0.15)" : "rgba(29,158,117,0.15)",
                                color: isExpiring ? "#EF9F27" : "#1D9E75",
                                border: `0.5px solid ${isExpiring ? "rgba(239,159,39,0.4)" : "rgba(29,158,117,0.4)"}`,
                              }}>{t.status}</span>
                            </div>
                            <p className="text-xs mb-2" style={{ color: branding.menu_text_color, opacity: 0.55 }}>Expires {t.expiry_label}</p>
                            <div className="rounded-full overflow-hidden mb-1" style={{ height: 3, background: "rgba(255,255,255,0.12)" }}>
                              <div className="rounded-full" style={{ height: 3, width: `${pct}%`, background: barColor }} />
                            </div>
                            <div className="flex justify-between">
                              <span className="text-xs" style={{ color: branding.menu_text_color, opacity: 0.55 }}>{t.remaining_ml}ml remaining</span>
                              <span className="text-xs" style={{ color: branding.menu_text_color, opacity: 0.4 }}>{t.bottle_size_ml}ml</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* Not found — name collection + empty state */}
              {bottlePhase === "not_found" && (
                <>
                  <div className="rounded-xl p-4 mb-3" style={{ background: "rgba(255,255,255,0.06)", border: "0.5px solid rgba(255,255,255,0.1)" }}>
                    <p className="text-sm font-semibold mb-1" style={{ color: branding.menu_text_color }}>What's your name?</p>
                    <p className="text-xs mb-3" style={{ color: branding.menu_text_color, opacity: 0.6 }}>We'll save your bottle to this number</p>
                    <input
                      type="text"
                      value={bottleNameInput}
                      onChange={(e) => { setBottleNameInput(e.target.value); setBottleNameErr(""); }}
                      placeholder="Your first name"
                      className="w-full rounded-lg px-3 py-2 mb-2"
                      style={{
                        background: "rgba(255,255,255,0.08)",
                        border: `0.5px solid ${bottleNameErr ? "#EF4444" : "rgba(255,255,255,0.15)"}`,
                        color: branding.menu_text_color,
                        outline: "none",
                        fontSize: 16,
                      }}
                    />
                    {bottleNameErr && <p className="text-xs text-red-400 mb-2">{bottleNameErr}</p>}
                    <button
                      type="button"
                      disabled={bottleNameSaving}
                      onClick={registerBottle}
                      className="w-full rounded-lg py-2 text-sm font-semibold"
                      style={{ background: primary, color: onPrimary(primary), border: "none", opacity: bottleNameSaving ? 0.6 : 1 }}
                    >
                      {bottleNameSaving ? "Saving..." : "Save & continue"}
                    </button>
                  </div>
                  <div className="flex items-start gap-3 rounded-xl p-4" style={{ background: "rgba(255,255,255,0.06)", border: "0.5px solid rgba(255,255,255,0.1)" }}>
                    <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(127,119,221,0.2)" }}>
                      <span style={{ fontSize: 16, color: primary }}>🍾</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold mb-1" style={{ color: branding.menu_text_color }}>No bottles stored yet</p>
                      <p className="text-xs leading-relaxed" style={{ color: branding.menu_text_color, opacity: 0.65 }}>
                        Buy a bottle. Store what's left. Saves you more than ordering peg by peg. Ask your server about our digital bottle locker.
                      </p>
                    </div>
                  </div>
                </>
              )}

            </div>
          </div>
        </>
      )}

      {/* === FOOTER === */}
      <footer
        className="border-t px-4 py-8 text-center"
        style={{ borderColor: "rgba(255,255,255,0.06)", color: branding.menu_accent_text_color }}
      >
        <p
          className="font-medium text-lg mb-1"
          style={{ fontFamily: "var(--font-editorial)", color: branding.menu_text_color }}
        >
          {data.venue.name}
        </p>
        <p className="text-[10px] uppercase tracking-[0.2em] mb-4" style={{ color: branding.menu_accent_text_color, fontFamily: "var(--font-body)" }}>
          Ask your server for details
        </p>
        <p className="text-[11px]" style={{ fontFamily: "var(--font-body)" }}>
          Powered by{" "}
          <a href="https://tabon.app" style={{ color: primary }} className="hover:underline">
            TabOn
          </a>
        </p>
      </footer>
    </div>
  );
}
